/**
 * METER READING FORM - PRODUCTION READY
 * Auto-populated dates, proper data loading/saving, calculated fields
 * @version 2.1.2 - Fixed setTimeout cleanup to prevent async errors
 * 
 * Note: If you see "runtime.lastError" in console, it's typically from a browser extension,
 * not from this code. This is a harmless Chrome extension warning.
 */

import React, { useState, useRef, useEffect, useLayoutEffect, useMemo, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Zap } from 'lucide-react';
import { motion } from 'framer-motion';
import {
  UniversalTable,
  useTableData,
  useTableNavigation,
  useAutoScroll,
  DEFAULT_STYLE_CONFIG,
  EXPANDED_STYLE_CONFIG
} from '../../../universal_table';
import { usePersistentTableData } from '../../../universal_table/hooks/usePersistentTableData';
import { FormActions, StatusBanner, SummaryModal, CorrectMistakeModal, DateRangeFilter } from '../../../shared';
import { METER_READING_COLUMNS, METER_PARAMETER_GROUPS } from '../forms_data/meterReadingColumns';
import { meterReadingService, MeterReadingDataRow, generalService, loadAllPaginatedData } from '../services/axonApiService';
import { useAuth } from '../../../../../../context/AuthContext';
import backendApi from '../../../../../../services/backendApi';
import { resolveUserFilterId, shouldLoadAllRecords } from '../utils/userDataAccess';
import { buildExcelUploadStorageKey, buildUserScopedStorageKey } from '../utils/storageKeys';
import { buildGenerationReportFooterText } from '../utils/generationReportFooter';
import { addRemovedRow, filterRemovedRows, syncRemovedRowsWithBackend, removeRemovedRow, clearRemovedRows } from '../utils/removedRowsManager';
import { normalizeNetImport, denormalizeNetImport } from '../utils/fieldMappers';
import { Edit } from 'lucide-react';
import { DeleteRowDialog } from '../components/DeleteRowDialog';

interface MeterReadingFormProps {
  site?: string;
}

export const MeterReadingForm: React.FC<MeterReadingFormProps> = ({ site: siteProp }) => {
  const MTR_DEBUG = false; // ⚡ Set true for debug logging, false for production speed
  const { site: siteFromParams } = useParams<{ site: string }>();
  const site = siteProp || siteFromParams;
  const siteCode = (site || 'axon').toLowerCase();
  const [isCondensed, setIsCondensed] = useState(true);
  const [status, setStatus] = useState<any>({ type: null, message: '' });
  const [isExporting, setIsExporting] = useState(false);
  const [showSummary, setShowSummary] = useState(false);
  const [summaryData, setSummaryData] = useState<MeterReadingDataRow[]>([]);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [showCorrectMistake, setShowCorrectMistake] = useState(false);
  const [correctMistakeData, setCorrectMistakeData] = useState<MeterReadingDataRow[]>([]);
  const [loadingCorrectMistake, setLoadingCorrectMistake] = useState(false);
  const [siteName, setSiteName] = useState<string>('axon');
  // Range Filter State
  const [startDate, setStartDate] = useState<string | null>(null);
  const [endDate, setEndDate] = useState<string | null>(null);
  const { user } = useAuth();
  const [isSaving, setIsSaving] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [rowToDelete, setRowToDelete] = useState<{ index: number; key: string } | null>(null);
  const [totalCountFromDB, setTotalCountFromDB] = useState<number | null>(null); // Total count from database

  const containerRef = useRef<HTMLDivElement>(null);
  const isManuallySortingRef = useRef(false);
  const [tableKey, setTableKey] = useState(0); // Force re-render key
  const previousRowKeysRef = useRef<string[]>([]); // Track previous rowKeys to detect changes
  const justAddedRowRef = useRef<string | null>(null); // Track newly added row to prevent real-time refresh from overwriting
  const hasScrolledToBottomRef = useRef(false); // Track if we've scrolled to bottom on initial load
  const [isTableVisible, setIsTableVisible] = useState(false); // Hide table until scroll is positioned
  const oldestLoadedDateRef = useRef<string | null>(null); // Track oldest date we've loaded for lazy loading
  const isLoadingMoreRef = useRef(false); // Track if we're currently loading more data
  const hasMoreDataRef = useRef(true); // Track if there's more data to load
  const SCROLL_TOP_LOAD_THRESHOLD = 260;
  const persistentStorageKey = useMemo(
    () => buildUserScopedStorageKey('unsaved_meterReading', siteCode, user),
    [siteCode, user?.username, user?.email, user?.fullName, user?.siteId]
  );

  const { data, rowKeys: hookRowKeys, updateCell, removeRow, setAllData } = usePersistentTableData(
    {},
    [],
    { storageKey: persistentStorageKey, enabled: true, debounceMs: 500 }
  );
  const [isTabVisible, setIsTabVisible] = useState(!document.hidden);

  // Track tab visibility to gate polling/refresh work
  useEffect(() => {
    const handleVisibility = () => setIsTabVisible(!document.hidden);
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, []);

  // Latest-value ref so effects (e.g. the real-time poll below) can read current
  // `data` without listing it as a dependency — avoids tearing down and re-arming
  // setInterval/setTimeout timers on every single keystroke.
  const dataRef = useRef(data);
  dataRef.current = data;

  // Local state to track rowKeys for immediate updates
  const [rowKeys, setRowKeysLocal] = useState<string[]>(hookRowKeys);
  const rowKeysRef = useRef(rowKeys);
  rowKeysRef.current = rowKeys;

  const footerText = useMemo(
    () => buildGenerationReportFooterText({
      siteName,
      sheetName: 'Plant Meters',
      dateKeys: rowKeys
    }),
    [siteName, rowKeys]
  );

  // Filter removed rows whenever data is restored from hook (e.g., when switching sheets)
  // This runs after initial load to catch data restored from localStorage
  const lastFilteredSignatureRef = useRef<string>('');
  const previousSiteRef = useRef<string>(siteCode);

  // Reset filter signature when site changes (switching between different sites)
  useEffect(() => {
    if (previousSiteRef.current !== siteCode) {
      previousSiteRef.current = siteCode;
      lastFilteredSignatureRef.current = '';
      MTR_DEBUG && console.log(`🔄 Site changed to ${siteCode}, resetting filter signature`);
    }
  }, [siteCode]);

  useEffect(() => {
    // Skip during initial load - that's handled in the initial load effect
    if (isInitialLoad) {
      lastFilteredSignatureRef.current = '';
      return;
    }

    if (hookRowKeys.length === 0) {
      lastFilteredSignatureRef.current = '';
      return;
    }

    // Create a signature for this data set to avoid filtering the same data multiple times
    const dataSignature = `${hookRowKeys.join(',')}_${Object.keys(data).length}`;
    if (lastFilteredSignatureRef.current === dataSignature) {
      return; // Already filtered this data set
    }

    // Check if we need to filter removed rows
    const filterData = async () => {
      try {
        const userResponse = await backendApi.getCurrentUser();
        let userIdForFilter: number | undefined;
        if (userResponse.success && userResponse.user?.id) {
          const backendUser = userResponse.user;
          const resolvedUserId = resolveUserFilterId(user, backendUser);
          userIdForFilter = resolvedUserId ?? Number(backendUser.id);
        }

        const { data: filteredData, rowKeys: filteredKeys } = filterRemovedRows(
          data,
          hookRowKeys,
          'Meter Reading',
          siteCode,
          userIdForFilter
        );

        // Create signature for filtered data
        const filteredSignature = `${filteredKeys.join(',')}_${Object.keys(filteredData).length}`;

        // Only update if filtering removed any rows
        if (filteredKeys.length !== hookRowKeys.length || JSON.stringify(filteredKeys) !== JSON.stringify(hookRowKeys)) {
          MTR_DEBUG && console.log(`🔍 Filtering restored data: ${hookRowKeys.length} -> ${filteredKeys.length} rows (${hookRowKeys.length - filteredKeys.length} removed)`);
          lastFilteredSignatureRef.current = filteredSignature;
          setAllData(filteredData, filteredKeys);
        } else {
          lastFilteredSignatureRef.current = dataSignature;
        }
      } catch (error) {
        MTR_DEBUG && console.warn('Failed to filter restored data:', error);
        lastFilteredSignatureRef.current = dataSignature;
      }
    };

    // Use a small delay to ensure data is fully loaded
    const timeoutId = setTimeout(filterData, 100);
    return () => clearTimeout(timeoutId);
  }, [hookRowKeys.join(','), Object.keys(data).length, siteCode, isInitialLoad]);

  // Sync local state with hook state (except when manually sorting)
  useEffect(() => {
    if (!isManuallySortingRef.current) {
      setRowKeysLocal(hookRowKeys);
      previousRowKeysRef.current = hookRowKeys;
    }
  }, [hookRowKeys]);

  // Track rowKeys changes and force re-render if order changed
  useEffect(() => {
    if (isInitialLoad || rowKeys.length === 0) {
      previousRowKeysRef.current = rowKeys;
      return;
    }

    // Check if the order changed (not just the content)
    const orderChanged = JSON.stringify(rowKeys) !== JSON.stringify(previousRowKeysRef.current);
    if (orderChanged && !isManuallySortingRef.current) {
      MTR_DEBUG && console.log('🔄 Detected rowKeys order change, forcing re-render');
      setTableKey(prev => prev + 1);
    }
    previousRowKeysRef.current = rowKeys;
  }, [rowKeys, isInitialLoad]);

  // Memoize rowKeys to ensure new reference when order changes
  const memoizedRowKeys = useMemo(() => {
    return Array.from(rowKeys); // Create new array reference
  }, [rowKeys.join(',')]); // Depend on the stringified version to detect order changes

  // Filter rowKeys by selected date range
  const filteredRowKeys = useMemo(() => {
    if (!startDate && !endDate) return rowKeys;

    return rowKeys.filter(key => {
      const rowDate = String(data[key]?.entry_date || data[key]?.Date || key).split('T')[0];

      // If only start date is selected
      if (startDate && !endDate) {
        return rowDate >= startDate;
      }

      // If only end date is selected
      if (!startDate && endDate) {
        return rowDate <= endDate;
      }

      // If both are selected
      if (startDate && endDate) {
        return rowDate >= startDate && rowDate <= endDate;
      }

      return true;
    });
  }, [rowKeys, data, startDate, endDate]);

  const mapMeterRowToForm = (row: any) => {
    const netImport = normalizeNetImport(row.net_import ?? row['Net Import']);
    return {
      id: row.id || row.pk || undefined,
      Date: row.entry_date,
      'Feeder1 Initial Export': row.feeder1_initial_export !== null && row.feeder1_initial_export !== undefined ? String(row.feeder1_initial_export) : '',
      'Feeder1 Final Export': row.feeder1_final_export !== null && row.feeder1_final_export !== undefined ? String(row.feeder1_final_export) : '',
      'Feeder1 Net kWh': row.feeder1_net_kwh !== null && row.feeder1_net_kwh !== undefined ? String(row.feeder1_net_kwh) : '',
      'Inverter Generation 600V': row.inverter_generation_600v !== null && row.inverter_generation_600v !== undefined ? String(row.inverter_generation_600v) : '',
      'Feeder1 Initial import kWh': row.feeder1_initial_import_kwh !== null && row.feeder1_initial_import_kwh !== undefined ? String(row.feeder1_initial_import_kwh) : '',
      'Feeder1 Final import kWh': row.feeder1_final_import_kwh !== null && row.feeder1_final_import_kwh !== undefined ? String(row.feeder1_final_import_kwh) : '',
      'B01Ff1 Net Import kWh': netImport
    };
  };

  useEffect(() => {
    if (!startDate && !endDate) return;
    if (isInitialLoad) return;

    let isCancelled = false;

    const loadFilteredData = async () => {
      try {
        const userResponse = await backendApi.getCurrentUser();
        if (!userResponse.success || !userResponse.user?.id || isCancelled) return;

        const backendUser = userResponse.user;
        const resolvedUserId = resolveUserFilterId(user, backendUser);
        const userIdForLoad = (resolvedUserId ?? Number(backendUser.id)) || 0;

        const rows = await loadAllPaginatedData(
          (params) => meterReadingService.loadByDateRange({
            userId: userIdForLoad,
            siteCode: siteCode,
            siteId: user?.siteId || backendUser?.site?.site_id || null,
            startDate: params.startDate,
            endDate: params.endDate,
            offset: params.offset,
            limit: params.limit
          }) as any,
          {
            startDate: startDate || undefined,
            endDate: endDate || undefined,
            initialLimit: 200
          }
        );

        if (isCancelled) return;

        const loadedData: Record<string, any> = {};
        const loadedKeys: string[] = [];

        rows.forEach((row: any) => {
          const dateKey = row.entry_date;
          if (!dateKey) return;
          loadedData[dateKey] = mapMeterRowToForm(row);
          if (!loadedKeys.includes(dateKey)) loadedKeys.push(dateKey);
        });

        loadedKeys.sort();
        setAllData(loadedData, loadedKeys);
      } catch (error) {
        console.error('Error loading filtered meter data:', error);
      }
    };

    loadFilteredData();
    return () => {
      isCancelled = true;
    };
  }, [startDate, endDate, isInitialLoad, siteCode, user, setAllData]);

  const { scrollToCell, registerInput } = useAutoScroll(containerRef);
  const { focusedCell, setFocusedCell, handleKeyDown } = useTableNavigation(
    rowKeys.length,
    METER_READING_COLUMNS.length,
    METER_READING_COLUMNS,
    scrollToCell
  );
  // Stable reference so memoized table rows aren't forced to re-render just because
  // this closure was recreated (setFocusedCell itself is already stable).
  const handleTableFocus = useCallback(
    (r: number, c: number) => setFocusedCell({ rowIndex: r, colIndex: c }),
    [setFocusedCell]
  );

  /**
   * Create empty row with all fields initialized
   */
  const createEmptyRow = (date: string) => {
    return {
      Date: date,
      'Feeder1 Initial Export': '',
      'Feeder1 Final Export': '',
      'Feeder1 Net kWh': '',
      'Inverter Generation 600V': '',
      'Feeder1 Initial import kWh': '',
      'Feeder1 Final import kWh': '',
      'B01Ff1 Net Import kWh': ''
    };
  };

  // Fetch site name from database
  useEffect(() => {
    const fetchSiteName = async () => {
      try {
        const response = await backendApi.getSites(false, siteCode.toUpperCase());
        if (response.success && Array.isArray(response.sites)) {
          const matchedSite = response.sites.find(
            (item: any) => item.site_code && item.site_code.toUpperCase() === siteCode.toUpperCase()
          ) || response.sites[0];

          if (matchedSite && matchedSite.site_name) {
            setSiteName(matchedSite.site_name);
          } else {
            setSiteName(siteCode.toUpperCase());
          }
        }
      } catch (error) {
        MTR_DEBUG && console.warn('Failed to fetch site name:', error);
        setSiteName(siteCode.toUpperCase());
      }
    };

    fetchSiteName();
  }, [siteCode]);

  /**
   * Initialize by loading user's existing data or Excel uploaded data
   * IMPORTANT: Check for persisted unsaved data first to preserve user's work
   * OPTIMIZED: Check sessionStorage cache to prevent reloading when switching sheets
   */
  useEffect(() => {
    if (!isInitialLoad) return;

    const loadUserData = async () => {
      try {
        // Clear temporary removals on initial page load so they reappear after refresh
        backendApi.getCurrentUser().then(response => {
          if (response.success && response.user?.id) {
            const backendUser = response.user;
            const resolvedUserId = resolveUserFilterId(user, backendUser);
            const userIdForStore = resolvedUserId ?? Number(backendUser.id);
            clearRemovedRows('Meter Reading', siteCode, userIdForStore);
            MTR_DEBUG && console.log('🧹 Cleared temporary removals on page load for Meter Reading');
          } else {
            clearRemovedRows('Meter Reading', siteCode);
            MTR_DEBUG && console.log('🧹 Cleared temporary removals on page load for Meter Reading');
          }
        }).catch(() => {
          clearRemovedRows('Meter Reading', siteCode);
          MTR_DEBUG && console.log('🧹 Cleared temporary removals on page load for Meter Reading');
        });

        // OPTIMIZATION: Check sessionStorage cache first to avoid reloading when switching sheets
        const cacheKey = `meterReading_cache_${siteCode}_${user?.username || 'anonymous'}`;
        const cachedDataStr = sessionStorage.getItem(cacheKey);

        if (cachedDataStr) {
          try {
            const cachedData = JSON.parse(cachedDataStr);
            const cacheTimestamp = cachedData.timestamp || 0;
            const cacheAge = Date.now() - cacheTimestamp;
            const CACHE_MAX_AGE = 5 * 60 * 1000; // 5 minutes

            // Use cache if it's less than 5 minutes old and has data
            if (cacheAge < CACHE_MAX_AGE && cachedData.data && cachedData.rowKeys && cachedData.rowKeys.length > 0) {
              MTR_DEBUG && console.log(`⚡ [CACHE HIT] Using cached Meter Reading data (${cachedData.rowKeys.length} rows, ${Math.round(cacheAge / 1000)}s old)`);
              setAllData(cachedData.data, cachedData.rowKeys);
              oldestLoadedDateRef.current = cachedData.oldestLoadedDate || null;
              hasMoreDataRef.current = cachedData.hasMoreData !== false;
              setIsInitialLoad(false);
              return;
            } else {
              MTR_DEBUG && console.log(`🗑️ [CACHE EXPIRED] Cache is ${Math.round(cacheAge / 1000)}s old, reloading...`);
              sessionStorage.removeItem(cacheKey);
            }
          } catch (e) {
            MTR_DEBUG && console.warn('Failed to parse cached data:', e);
            sessionStorage.removeItem(cacheKey);
          }
        }

        // First, check if persisted data was already restored by usePersistentTableData hook
        // Only use persisted data if it has actual meaningful data (not just empty rows)
        const hasMeaningfulData = rowKeys.some(key => {
          const row = data[key];
          if (!row) return false;
          // Check if row has any non-empty values (excluding Date)
          return Object.keys(row).some(k =>
            k !== 'Date' &&
            row[k] !== null &&
            row[k] !== undefined &&
            row[k] !== ''
          );
        });

        // Always load from backend to ensure we have the latest data (including newly uploaded data)
        // Persisted data is mainly for unsaved changes, not for replacing backend data
        if (hasMeaningfulData) {
          MTR_DEBUG && console.log(`ℹ️ Found persisted Meter Reading data (${rowKeys.length} rows), but will load from backend to ensure latest data`);
          // Don't return - continue to load from backend
          // The backend data will be merged with persisted data if needed
        }

        // If persisted data exists but is empty, clear it and start fresh
        if (rowKeys.length > 0) {
          MTR_DEBUG && console.log(`⚠️ Persisted Meter Reading data contains only empty rows, clearing and starting fresh`);
          try {
            localStorage.removeItem(persistentStorageKey);
            setAllData({}, []); // Clear empty rows
          } catch (error) {
            MTR_DEBUG && console.warn('Failed to clear empty persisted data:', error);
          }
        }

        // Second, check for Excel uploaded data
        if (site) {
          const storageKey = buildExcelUploadStorageKey(site, user);
          const excelDataStr = localStorage.getItem(storageKey);

          if (excelDataStr) {
            try {
              const excelData = JSON.parse(excelDataStr);
              const meterData = excelData['Meter Reading'];

              if (meterData && Object.keys(meterData).length > 0) {
                let loadedData: any = {};
                const allKeys: string[] = [];

                Object.entries(meterData).forEach(([dateKey, rowData]: [string, any]) => {
                  loadedData[dateKey] = {
                    Date: dateKey,
                    'Feeder1 Initial Export': rowData['Feeder1 Initial Export'] || '',
                    'Feeder1 Final Export': rowData['Feeder1 Final Export'] || '',
                    'Feeder1 Net kWh': rowData['Feeder1 Net kWh'] || '',
                    'Inverter Generation 600V': rowData['Inverter Generation 600V'] || '',
                    'Feeder1 Initial import kWh': rowData['Feeder1 Initial import kWh'] || '',
                    'Feeder1 Final import kWh': rowData['Feeder1 Final import kWh'] || '',
                    'B01Ff1 Net Import kWh': rowData['B01Ff1 Net Import kWh'] || ''
                  };
                  // Add all dates (including today) to rowKeys
                  if (!allKeys.includes(dateKey)) {
                    allKeys.push(dateKey);
                  }
                });

                // Sort all dates (ascending)
                allKeys.sort();

                // Show ALL Excel dates for initial load
                const finalKeys = [...allKeys].sort(); // Sort ascending for display

                // Track oldest date we've loaded for lazy loading
                if (finalKeys.length > 0) {
                  oldestLoadedDateRef.current = finalKeys[0];
                  hasMoreDataRef.current = true;
                  MTR_DEBUG && console.log(`📦 [LAZY LOADING] Excel initial load: ${finalKeys.length} dates (all), oldest: ${oldestLoadedDateRef.current}, hasMore: ${hasMoreDataRef.current}`);
                }

                // Filter loadedData to only include the dates we're keeping
                const filteredLoadedData: any = {};
                finalKeys.forEach(key => {
                  if (loadedData[key]) {
                    filteredLoadedData[key] = loadedData[key];
                  }
                });
                loadedData = filteredLoadedData;

                // Filter out removed rows
                const userResponse = await backendApi.getCurrentUser();
                let userIdForFilter: number | undefined;
                if (userResponse.success && userResponse.user?.id) {
                  const backendUser = userResponse.user;
                  const resolvedUserId = resolveUserFilterId(user, backendUser);
                  userIdForFilter = resolvedUserId ?? Number(backendUser.id);
                }
                const { data: filteredData, rowKeys: filteredKeys } = filterRemovedRows(
                  loadedData,
                  finalKeys,
                  'Meter Reading',
                  siteCode,
                  userIdForFilter
                );

                setAllData(filteredData, filteredKeys);
                localStorage.removeItem(storageKey);
                setStatus({ type: 'success', message: `✅ Loaded ${filteredKeys.length} rows from Excel upload (${allKeys.length - filteredKeys.length} removed from view)` });
                MTR_DEBUG && console.log(`✅ Loaded ${filteredKeys.length} rows from Excel for Meter Reading (${allKeys.length - filteredKeys.length} removed)`);
                setIsInitialLoad(false);
                return;
              }
            } catch (excelError) {
              console.error('Error parsing Excel data:', excelError);
            }
          }
        }

        // If no Excel data, load from backend
        // Get current user ID
        const userResponse = await backendApi.getCurrentUser();
        if (userResponse.success && userResponse.user?.id) {
          const backendUser = userResponse.user;
          const resolvedUserId = resolveUserFilterId(user, backendUser);
          const userIdForLoad = (resolvedUserId ?? Number(backendUser.id)) || 0;
          const isAdmin = shouldLoadAllRecords(user, backendUser);

          MTR_DEBUG && console.log(`📥 [INITIAL LOAD] isAdmin: ${isAdmin}, userIdForLoad: ${userIdForLoad}, site: ${site}`);

          if (resolvedUserId === null && userIdForLoad === 0) {
            MTR_DEBUG && console.warn('⚠️ Unable to resolve Meter Reading user ID - defaulting to admin view (all data)');
          }

          let loadedData: any = {};
          let loadedKeys: string[] = [];

          if (isAdmin) {
            // Admin: Load only latest data for the site using get-dgr endpoint (LAZY LOADING)
            const API_BASE_URL = 'http://localhost:8000/api/v1';

            // LAZY LOADING: Fetch all recent data for initial display
            const endDate = new Date();
            const startDate = new Date();
            startDate.setDate(startDate.getDate() - 730); // 2 years - ensures ALL data found on FIRST call
            const start = startDate.toISOString().split('T')[0];
            const end = endDate.toISOString().split('T')[0];

            MTR_DEBUG && console.log(`📥 [INITIAL LOAD] Fetching latest data from get-dgr for ${siteCode} (${start} to ${end})...`);

            try {
              const token = sessionStorage.getItem('access_token');
              const response = await fetch(`${API_BASE_URL}/get-dgr/${siteCode}/?sheet=Meter Reading&limit=500`, {
                headers: { 'Authorization': `Bearer ${token}` }
              });
              MTR_DEBUG && console.log(`📥 [INITIAL LOAD] Response status: ${response.status}`);
              if (response.ok) {
                const payload = await response.json();
                MTR_DEBUG && console.log(`📥 [INITIAL LOAD] Payload:`, payload);
                const meterData = payload?.data?.meter || [];
                MTR_DEBUG && console.log(`📥 [INITIAL LOAD] Found ${meterData.length} meter records`);
                if (meterData.length > 0) {
                  MTR_DEBUG && console.log(`📥 [INITIAL LOAD] First record:`, meterData[0]);
                  MTR_DEBUG && console.log(`📥 [INITIAL LOAD] First record keys:`, Object.keys(meterData[0]));
                }

                meterData.forEach((row: any) => {
                  const dateKey = row.entry_date;
                  if (dateKey) {
                    // Handle Net Import field - serializer returns net_import (model field), not Net Import (DB column)
                    const netImport = row.net_import !== null && row.net_import !== undefined
                      ? String(row.net_import)
                      : (row['Net Import'] !== null && row['Net Import'] !== undefined
                        ? String(row['Net Import'])
                        : '');

                    loadedData[dateKey] = {
                      id: row.id || row.pk || undefined, // Store ID for updates
                      Date: dateKey,
                      'Feeder1 Initial Export': row.feeder1_initial_export !== null && row.feeder1_initial_export !== undefined ? String(row.feeder1_initial_export) : '',
                      'Feeder1 Final Export': row.feeder1_final_export !== null && row.feeder1_final_export !== undefined ? String(row.feeder1_final_export) : '',
                      'Feeder1 Net kWh': row.feeder1_net_kwh !== null && row.feeder1_net_kwh !== undefined ? String(row.feeder1_net_kwh) : '',
                      'Inverter Generation 600V': row.inverter_generation_600v !== null && row.inverter_generation_600v !== undefined ? String(row.inverter_generation_600v) : '',
                      'Feeder1 Initial import kWh': row.feeder1_initial_import_kwh !== null && row.feeder1_initial_import_kwh !== undefined ? String(row.feeder1_initial_import_kwh) : '',
                      'Feeder1 Final import kWh': row.feeder1_final_import_kwh !== null && row.feeder1_final_import_kwh !== undefined ? String(row.feeder1_final_import_kwh) : '',
                      'B01Ff1 Net Import kWh': netImport
                    };
                    if (!loadedKeys.includes(dateKey)) {
                      loadedKeys.push(dateKey);
                    }
                  } else {
                    MTR_DEBUG && console.warn(`⚠️ [INITIAL LOAD] Row missing entry_date:`, row);
                  }
                });

                MTR_DEBUG && console.log(`📥 [INITIAL LOAD] Processed ${loadedKeys.length} dates, loadedData keys:`, Object.keys(loadedData));

                // Show ALL returned dates for initial load (backend limit=500 handles pagination)
                if (loadedKeys.length > 0) {
                  loadedKeys.sort();
                  const finalKeys = [...loadedKeys].sort();

                  // Track oldest date we've loaded for lazy loading
                  if (finalKeys.length > 0) {
                    oldestLoadedDateRef.current = finalKeys[0];
                    hasMoreDataRef.current = true; // Always true initially
                    MTR_DEBUG && console.log(`📦 [LAZY LOADING] Admin initial load: ${finalKeys.length} dates (all returned), oldest: ${oldestLoadedDateRef.current}, hasMore: ${hasMoreDataRef.current}`);
                  }

                  loadedKeys = finalKeys;
                }
              } else {
                const errorText = await response.text();
                console.error(`❌ [INITIAL LOAD] get-dgr failed: ${response.status} - ${errorText}`);
              }
            } catch (error) {
              console.error('Error loading all data via get-dgr:', error);
              // Fallback to loadByUser if get-dgr fails
              const loadResult = await meterReadingService.loadByUser(userIdForLoad);
              MTR_DEBUG && console.log('🔄 Fallback load result:', { ok: loadResult.ok, dataLength: loadResult.data?.length });

              if (loadResult.ok && loadResult.data && loadResult.data.length > 0) {
                loadResult.data.forEach((row: any) => {
                  const dateKey = row.entry_date;
                  if (dateKey) {
                    loadedData[dateKey] = {
                      id: row.id || row.pk || undefined,
                      Date: dateKey,
                      'Feeder1 Initial Export': row.feeder1_initial_export !== null && row.feeder1_initial_export !== undefined ? String(row.feeder1_initial_export) : '',
                      'Feeder1 Final Export': row.feeder1_final_export !== null && row.feeder1_final_export !== undefined ? String(row.feeder1_final_export) : '',
                      'Feeder1 Net kWh': row.feeder1_net_kwh !== null && row.feeder1_net_kwh !== undefined ? String(row.feeder1_net_kwh) : '',
                      'Inverter Generation 600V': row.inverter_generation_600v !== null && row.inverter_generation_600v !== undefined ? String(row.inverter_generation_600v) : '',
                      'Feeder1 Initial import kWh': row.feeder1_initial_import_kwh !== null && row.feeder1_initial_import_kwh !== undefined ? String(row.feeder1_initial_import_kwh) : '',
                      'Feeder1 Final import kWh': row.feeder1_final_import_kwh !== null && row.feeder1_final_import_kwh !== undefined ? String(row.feeder1_final_import_kwh) : '',
                      'B01Ff1 Net Import kWh': (row['Net Import'] !== null && row['Net Import'] !== undefined ? String(row['Net Import']) : (row.net_import !== null && row.net_import !== undefined ? String(row.net_import) : ''))
                    };
                    if (!loadedKeys.includes(dateKey)) {
                      loadedKeys.push(dateKey);
                    }
                  }
                });
              }
            }
          } else {
            // Regular user: Load only latest data (LAZY LOADING)
            // SUPERFAST: 2-year range covers ALL historical data
            const endDate = new Date();
            const startDate = new Date();
            startDate.setDate(startDate.getDate() - 730); // 2 years - ensures ALL data found on FIRST call
            const start = startDate.toISOString().split('T')[0];
            const end = endDate.toISOString().split('T')[0];

            MTR_DEBUG && console.log(`📥 [INITIAL LOAD] Loading latest Meter Reading data for user ${userIdForLoad} (${start} to ${end})...`);
            const API_BASE_URL = 'http://localhost:8000/api/v1';
            const token = sessionStorage.getItem('access_token');
            const authHeaders: HeadersInit = {};
            if (token) authHeaders['Authorization'] = `Bearer ${token}`;
            const response = await fetch(`${API_BASE_URL}/meter-reading/range/?start_date=${start}&end_date=${end}`, { headers: authHeaders });

            if (response.ok) {
              const payload = await response.json();
              const meterData = Array.isArray(payload) ? payload : (payload?.data || []);

              // Filter by user
              const userData = meterData.filter((row: any) => {
                const rowUserId = row.user_id || row.user?.id;
                return rowUserId === userIdForLoad;
              });

              MTR_DEBUG && console.log(`📥 [INITIAL LOAD] Found ${userData.length} records for user ${userIdForLoad}`);

              userData.forEach((row: any) => {
                const dateKey = row.entry_date;
                if (dateKey) {
                  const netImport = row.net_import !== null && row.net_import !== undefined
                    ? String(row.net_import)
                    : (row['Net Import'] !== null && row['Net Import'] !== undefined
                      ? String(row['Net Import'])
                      : '');

                  loadedData[dateKey] = {
                    id: row.id || row.pk || undefined,
                    Date: dateKey,
                    'Feeder1 Initial Export': row.feeder1_initial_export !== null && row.feeder1_initial_export !== undefined ? String(row.feeder1_initial_export) : '',
                    'Feeder1 Final Export': row.feeder1_final_export !== null && row.feeder1_final_export !== undefined ? String(row.feeder1_final_export) : '',
                    'Feeder1 Net kWh': row.feeder1_net_kwh !== null && row.feeder1_net_kwh !== undefined ? String(row.feeder1_net_kwh) : '',
                    'Inverter Generation 600V': row.inverter_generation_600v !== null && row.inverter_generation_600v !== undefined ? String(row.inverter_generation_600v) : '',
                    'Feeder1 Initial import kWh': row.feeder1_initial_import_kwh !== null && row.feeder1_initial_import_kwh !== undefined ? String(row.feeder1_initial_import_kwh) : '',
                    'Feeder1 Final import kWh': row.feeder1_final_import_kwh !== null && row.feeder1_final_import_kwh !== undefined ? String(row.feeder1_final_import_kwh) : '',
                    'B01Ff1 Net Import kWh': netImport
                  };
                  if (!loadedKeys.includes(dateKey)) {
                    loadedKeys.push(dateKey);
                  }
                }
              });

              MTR_DEBUG && console.log(`📥 [INITIAL LOAD] Processed ${loadedKeys.length} dates from range endpoint`);

              // Show ALL returned dates for initial load (2-year window covers all historical data)
              if (loadedKeys.length > 0) {
                loadedKeys.sort();
                const finalKeys = [...loadedKeys].sort();

                // Track oldest date we've loaded for lazy loading
                if (finalKeys.length > 0) {
                  oldestLoadedDateRef.current = finalKeys[0];
                  hasMoreDataRef.current = true; // Always true initially
                  MTR_DEBUG && console.log(`📦 [LAZY LOADING] User initial load: ${finalKeys.length} dates (all returned), oldest: ${oldestLoadedDateRef.current}, hasMore: ${hasMoreDataRef.current}`);
                }

                loadedKeys = finalKeys;
              }
            } else {
              // Fallback to loadByUser if range endpoint fails
              MTR_DEBUG && console.log(`📥 Loading Meter Reading data via loadByUser for user ${userIdForLoad}...`);
              const loadResult = await meterReadingService.loadByUser(userIdForLoad);
              MTR_DEBUG && console.log('🔄 Load result:', { ok: loadResult.ok, dataLength: loadResult.data?.length, error: loadResult.error });

              if (loadResult.ok && loadResult.data && loadResult.data.length > 0) {
                MTR_DEBUG && console.log(`📥 [INITIAL LOAD] loadByUser returned ${loadResult.data.length} records`);
                MTR_DEBUG && console.log(`📥 [INITIAL LOAD] First record from loadByUser:`, loadResult.data[0]);
                loadResult.data.forEach((row: any) => {
                  const dateKey = row.entry_date;
                  if (dateKey) {
                    // Handle Net Import field - serializer returns net_import (model field)
                    const netImport = row.net_import !== null && row.net_import !== undefined
                      ? String(row.net_import)
                      : (row['Net Import'] !== null && row['Net Import'] !== undefined
                        ? String(row['Net Import'])
                        : '');

                    loadedData[dateKey] = {
                      id: row.id || row.pk || undefined,
                      Date: dateKey,
                      'Feeder1 Initial Export': row.feeder1_initial_export !== null && row.feeder1_initial_export !== undefined ? String(row.feeder1_initial_export) : '',
                      'Feeder1 Final Export': row.feeder1_final_export !== null && row.feeder1_final_export !== undefined ? String(row.feeder1_final_export) : '',
                      'Feeder1 Net kWh': row.feeder1_net_kwh !== null && row.feeder1_net_kwh !== undefined ? String(row.feeder1_net_kwh) : '',
                      'Inverter Generation 600V': row.inverter_generation_600v !== null && row.inverter_generation_600v !== undefined ? String(row.inverter_generation_600v) : '',
                      'Feeder1 Initial import kWh': row.feeder1_initial_import_kwh !== null && row.feeder1_initial_import_kwh !== undefined ? String(row.feeder1_initial_import_kwh) : '',
                      'Feeder1 Final import kWh': row.feeder1_final_import_kwh !== null && row.feeder1_final_import_kwh !== undefined ? String(row.feeder1_final_import_kwh) : '',
                      'B01Ff1 Net Import kWh': netImport
                    };
                    if (!loadedKeys.includes(dateKey)) {
                      loadedKeys.push(dateKey);
                    }
                  } else {
                    MTR_DEBUG && console.warn(`⚠️ [INITIAL LOAD] Row missing entry_date:`, row);
                  }
                });

                // Show ALL returned dates from loadByUser (no artificial cap)
                if (loadedKeys.length > 0) {
                  loadedKeys.sort();
                  const finalKeys = [...loadedKeys].sort();

                  if (finalKeys.length > 0) {
                    oldestLoadedDateRef.current = finalKeys[0];
                    hasMoreDataRef.current = true;
                  }

                  loadedKeys = finalKeys;
                }

                MTR_DEBUG && console.log(`📥 [INITIAL LOAD] Processed ${loadedKeys.length} dates from loadByUser`);
              } else {
                MTR_DEBUG && console.log(`⚠️ [INITIAL LOAD] loadByUser returned no data - ok: ${loadResult.ok}, dataLength: ${loadResult.data?.length}, error: ${loadResult.error}`);
              }
            }
          }

          // Fetch total count from database for footer display
          try {
            const totalCountResult = await meterReadingService.loadByUser(userIdForLoad);
            if (totalCountResult.ok && totalCountResult.data) {
              const uniqueDates = new Set(totalCountResult.data.map((r: any) => r.entry_date).filter(Boolean));
              setTotalCountFromDB(uniqueDates.size);
              MTR_DEBUG && console.log(`📊 [TOTAL COUNT] Total dates from database: ${uniqueDates.size}`);
            }
          } catch (error) {
            MTR_DEBUG && console.warn('Failed to fetch total count:', error);
          }

          // Process loaded data (lazy loading already applied in each branch above)
          // Sort all dates (ascending) - data is already filtered to latest 20 in each branch
          loadedKeys.sort();
          const finalKeys = [...loadedKeys].sort();

          MTR_DEBUG && console.log(`📥 [INITIAL LOAD] Before filtering - loadedData keys:`, Object.keys(loadedData));
          MTR_DEBUG && console.log(`📥 [INITIAL LOAD] Before filtering - finalKeys:`, finalKeys);
          if (finalKeys.length > 0) {
            MTR_DEBUG && console.log(`📥 [INITIAL LOAD] Sample loadedData entry:`, loadedData[finalKeys[0]]);
          }

          if (Object.keys(loadedData).length > 0) {
            // Sync removed rows with backend - restore any rows that exist in backend
            syncRemovedRowsWithBackend(finalKeys, 'Meter Reading', siteCode, userIdForLoad);

            // Filter out removed rows (persists across sheet switches)
            // Rows that exist in backend will be automatically restored
            const { data: filteredData, rowKeys: filteredKeys } = filterRemovedRows(
              loadedData,
              finalKeys,
              'Meter Reading',
              siteCode,
              userIdForLoad,
              { restoreBackendRows: true } // Restore backend rows on initial load since we cleared temporary removals
            );

            MTR_DEBUG && console.log(`📥 [INITIAL LOAD] After filtering - filteredKeys:`, filteredKeys);
            MTR_DEBUG && console.log(`📥 [INITIAL LOAD] After filtering - filteredData keys:`, Object.keys(filteredData));
            if (filteredKeys.length > 0) {
              MTR_DEBUG && console.log(`📥 [INITIAL LOAD] Sample filteredData entry:`, filteredData[filteredKeys[0]]);
            }

            // Clear persisted data first to avoid conflicts
            try {
              localStorage.removeItem(persistentStorageKey);
              MTR_DEBUG && console.log('🧹 [INITIAL LOAD] Cleared persisted data to avoid conflicts');
            } catch (e) {
              MTR_DEBUG && console.warn('Failed to clear persisted data:', e);
            }

            setAllData(filteredData, filteredKeys);

            // OPTIMIZATION: Cache the loaded data in sessionStorage
            try {
              const cacheKey = `meterReading_cache_${siteCode}_${user?.username || 'anonymous'}`;
              const cacheData = {
                data: filteredData,
                rowKeys: filteredKeys,
                oldestLoadedDate: oldestLoadedDateRef.current,
                hasMoreData: hasMoreDataRef.current,
                timestamp: Date.now()
              };
              sessionStorage.setItem(cacheKey, JSON.stringify(cacheData));
              MTR_DEBUG && console.log(`💾 [CACHE] Cached Meter Reading data (${filteredKeys.length} rows)`);
            } catch (e) {
              MTR_DEBUG && console.warn('Failed to cache data:', e);
            }

            MTR_DEBUG && console.log(
              `✅ [INITIAL LOAD] Set ${filteredKeys.length} records (${finalKeys.length - filteredKeys.length} removed from view) for ${userIdForLoad <= 0 ? 'all users (admin view)' : `user ${userIdForLoad}`
              }`
            );
          } else {
            // No existing data
            MTR_DEBUG && console.log(`⚠️ [INITIAL LOAD] loadedData is empty - Object.keys(loadedData).length = ${Object.keys(loadedData).length}`);
            MTR_DEBUG && console.log(`⚠️ [INITIAL LOAD] loadedKeys.length = ${loadedKeys.length}`);
            setAllData({}, []);
            MTR_DEBUG && console.log(`✅ No existing data`);
          }
        } else {
          // User not found
          setAllData({}, []);
          MTR_DEBUG && console.log(`✅ User not found`);
        }
      } catch (error: any) {
        console.error('❌ Error loading user data:', error);
        // On error, set empty data
        setAllData({}, []);
      } finally {
        setIsInitialLoad(false);
      }
    };

    loadUserData();
  }, [isInitialLoad, setAllData, site]);

  /**
   * Load more older data when user scrolls up (lazy loading)
   */
  const loadMoreOlderData = async () => {
    if (isLoadingMoreRef.current || !hasMoreDataRef.current || !oldestLoadedDateRef.current) {
      return;
    }

    // Disable lazy loading when date filter is active
    if (startDate || endDate) {
      MTR_DEBUG && console.log(`⏸️ [SCROLL HANDLER] Not attaching - date filter active`);
      return;
    }

    isLoadingMoreRef.current = true;
    MTR_DEBUG && console.log(`📦 [LAZY LOADING] Loading more data older than ${oldestLoadedDateRef.current}`);

    try {
      const userResponse = await backendApi.getCurrentUser();
      if (!userResponse.success || !userResponse.user?.id) {
        isLoadingMoreRef.current = false;
        return;
      }

      const backendUser = userResponse.user;
      const resolvedUserId = resolveUserFilterId(user, backendUser);
      const userIdForLoad = (resolvedUserId ?? Number(backendUser.id)) || 0;
      // Fetch 30 days before oldest loaded date for lazy loading
      const endDate = new Date(oldestLoadedDateRef.current);
      endDate.setDate(endDate.getDate() - 1); // One day before oldest loaded date
      const startDate = new Date(endDate);
      startDate.setDate(startDate.getDate() - 30); // 30 days back for substantial lazy load chunks
      const start = startDate.toISOString().split('T')[0];
      const end = endDate.toISOString().split('T')[0];

      let loadedData: any = {};
      let loadedKeys: string[] = [];

      const rows = await loadAllPaginatedData(
        (params) => meterReadingService.loadByDateRange({
          userId: userIdForLoad,
          siteCode: siteCode,
          siteId: user?.siteId || backendUser?.site?.site_id || null,
          startDate: params.startDate,
          endDate: params.endDate,
          offset: params.offset,
          limit: params.limit
        }) as any,
        {
          startDate: start,
          endDate: end,
          initialLimit: 200
        }
      );

      rows.forEach((row: any) => {
        const dateKey = row.entry_date;
        if (dateKey && dateKey < oldestLoadedDateRef.current!) {
          const netImport = row.net_import !== null && row.net_import !== undefined
            ? String(row.net_import)
            : (row['Net Import'] !== null && row['Net Import'] !== undefined
              ? String(row['Net Import'])
              : '');

          loadedData[dateKey] = {
            id: row.id || row.pk || undefined,
            Date: dateKey,
            'Feeder1 Initial Export': row.feeder1_initial_export !== null && row.feeder1_initial_export !== undefined ? String(row.feeder1_initial_export) : '',
            'Feeder1 Final Export': row.feeder1_final_export !== null && row.feeder1_final_export !== undefined ? String(row.feeder1_final_export) : '',
            'Feeder1 Net kWh': row.feeder1_net_kwh !== null && row.feeder1_net_kwh !== undefined ? String(row.feeder1_net_kwh) : '',
            'Inverter Generation 600V': row.inverter_generation_600v !== null && row.inverter_generation_600v !== undefined ? String(row.inverter_generation_600v) : '',
            'Feeder1 Initial import kWh': row.feeder1_initial_import_kwh !== null && row.feeder1_initial_import_kwh !== undefined ? String(row.feeder1_initial_import_kwh) : '',
            'Feeder1 Final import kWh': row.feeder1_final_import_kwh !== null && row.feeder1_final_import_kwh !== undefined ? String(row.feeder1_final_import_kwh) : '',
            'B01Ff1 Net Import kWh': netImport
          };
          if (!loadedKeys.includes(dateKey)) {
            loadedKeys.push(dateKey);
          }
        }
      });

      if (loadedKeys.length > 0) {
        // Take ALL newly loaded dates (no artificial cap)
        loadedKeys.sort();
        const newKeysToAdd = loadedKeys;

        // SCROLL PRESERVATION: Save scroll state before prepending data
        const container = containerRef.current;
        const prevScrollHeight = container ? container.scrollHeight : 0;
        const prevScrollTop = container ? container.scrollTop : 0;

        // Merge with existing data
        const currentData = { ...data };
        const currentKeys = [...rowKeys];

        newKeysToAdd.forEach(key => {
          if (!currentKeys.includes(key) && loadedData[key]) {
            currentData[key] = loadedData[key];
            currentKeys.push(key);
          }
        });

        // Sort all keys and update oldest date
        currentKeys.sort();
        oldestLoadedDateRef.current = currentKeys[0];

        // There might be more data as long as we got any records back
        hasMoreDataRef.current = true;

        // OPTIMIZATION: Update cache with new data
        try {
          const cacheKey = `meterReading_cache_${siteCode}_${user?.username || 'anonymous'}`;
          const cacheData = {
            data: currentData,
            rowKeys: currentKeys,
            oldestLoadedDate: oldestLoadedDateRef.current,
            hasMoreData: hasMoreDataRef.current,
            timestamp: Date.now()
          };
          sessionStorage.setItem(cacheKey, JSON.stringify(cacheData));
        } catch (e) {
          MTR_DEBUG && console.warn('Failed to update cache:', e);
        }

        // Update data
        setAllData(currentData, currentKeys);

        // SCROLL PRESERVATION: Restore scroll position after DOM updates
        requestAnimationFrame(() => {
          if (containerRef.current) {
            const newScrollHeight = containerRef.current.scrollHeight;
            const scrollDiff = newScrollHeight - prevScrollHeight;
            containerRef.current.scrollTop = prevScrollTop + scrollDiff;
          }
        });

        MTR_DEBUG && console.log(`📦 [LAZY LOADING] Loaded ${newKeysToAdd.length} older dates, oldest now: ${oldestLoadedDateRef.current}, hasMore: ${hasMoreDataRef.current}`);
      } else {
        // No more data
        hasMoreDataRef.current = false;
        MTR_DEBUG && console.log(`📦 [LAZY LOADING] No more data to load`);
      }
    } catch (error) {
      console.error('Error loading more data:', error);
    } finally {
      isLoadingMoreRef.current = false;
    }
  };

  /**
   * Real-time data refresh - Poll database every 30 seconds for fresh data
   */
  useEffect(() => {
    if (isInitialLoad) return; // Don't poll during initial load

    const loadFreshData = async () => {
      if (document.hidden) return;
      // Check freshly (via ref) whether a row was just added — evaluated per poll tick,
      // not just once when this effect was set up, so it stays accurate without needing
      // `rowKeys` in the dependency array below.
      if (justAddedRowRef.current && rowKeysRef.current.includes(justAddedRowRef.current)) {
        MTR_DEBUG && console.log(`⏸️ [REAL-TIME] Skipping refresh - new row just added: ${justAddedRowRef.current}`);
        return; // Don't refresh if a new row was just added
      }
      try {
        const userResponse = await backendApi.getCurrentUser();
        if (!userResponse.success || !userResponse.user?.id) return;

        const backendUser = userResponse.user;
        const resolvedUserId = resolveUserFilterId(user, backendUser);
        const userIdForLoad = (resolvedUserId ?? Number(backendUser.id)) || 0;
        const isAdmin = shouldLoadAllRecords(user, backendUser);

        let loadedData: any = {};
        let loadedKeys: string[] = [];

        if (isAdmin) {
          // Admin: Load all data for the site using get-dgr endpoint
          try {
            const API_BASE_URL = 'http://localhost:8000/api/v1';
            const token = sessionStorage.getItem('access_token');
            const response = await fetch(`${API_BASE_URL}/get-dgr/${siteCode}/?sheet=Meter Reading`, {
              headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
              const payload = await response.json();
              const meterData = payload?.data?.meter || [];

              meterData.forEach((row: any) => {
                const dateKey = row.entry_date;
                if (dateKey) {
                  const netImport = row.net_import !== null && row.net_import !== undefined
                    ? String(row.net_import)
                    : (row['Net Import'] !== null && row['Net Import'] !== undefined
                      ? String(row['Net Import'])
                      : '');

                  loadedData[dateKey] = {
                    id: row.id || row.pk || undefined,
                    Date: dateKey,
                    'Feeder1 Initial Export': row.feeder1_initial_export !== null && row.feeder1_initial_export !== undefined ? String(row.feeder1_initial_export) : '',
                    'Feeder1 Final Export': row.feeder1_final_export !== null && row.feeder1_final_export !== undefined ? String(row.feeder1_final_export) : '',
                    'Feeder1 Net kWh': row.feeder1_net_kwh !== null && row.feeder1_net_kwh !== undefined ? String(row.feeder1_net_kwh) : '',
                    'Inverter Generation 600V': row.inverter_generation_600v !== null && row.inverter_generation_600v !== undefined ? String(row.inverter_generation_600v) : '',
                    'Feeder1 Initial import kWh': row.feeder1_initial_import_kwh !== null && row.feeder1_initial_import_kwh !== undefined ? String(row.feeder1_initial_import_kwh) : '',
                    'Feeder1 Final import kWh': row.feeder1_final_import_kwh !== null && row.feeder1_final_import_kwh !== undefined ? String(row.feeder1_final_import_kwh) : '',
                    'B01Ff1 Net Import kWh': netImport
                  };
                  if (!loadedKeys.includes(dateKey)) {
                    loadedKeys.push(dateKey);
                  }
                }
              });
            }
          } catch (error) {
            console.error('Error refreshing data via get-dgr:', error);
            return; // Don't update on error
          }
        } else {
          // Regular user: Load only their data
          const loadResult = await meterReadingService.loadByUser(userIdForLoad);
          if (loadResult.ok && loadResult.data && loadResult.data.length > 0) {
            loadResult.data.forEach((row: any) => {
              const dateKey = row.entry_date;
              if (dateKey) {
                const netImport = row.net_import !== null && row.net_import !== undefined
                  ? String(row.net_import)
                  : (row['Net Import'] !== null && row['Net Import'] !== undefined
                    ? String(row['Net Import'])
                    : '');

                loadedData[dateKey] = {
                  id: row.id || row.pk || undefined,
                  Date: dateKey,
                  'Feeder1 Initial Export': row.feeder1_initial_export !== null && row.feeder1_initial_export !== undefined ? String(row.feeder1_initial_export) : '',
                  'Feeder1 Final Export': row.feeder1_final_export !== null && row.feeder1_final_export !== undefined ? String(row.feeder1_final_export) : '',
                  'Feeder1 Net kWh': row.feeder1_net_kwh !== null && row.feeder1_net_kwh !== undefined ? String(row.feeder1_net_kwh) : '',
                  'Inverter Generation 600V': row.inverter_generation_600v !== null && row.inverter_generation_600v !== undefined ? String(row.inverter_generation_600v) : '',
                  'Feeder1 Initial import kWh': row.feeder1_initial_import_kwh !== null && row.feeder1_initial_import_kwh !== undefined ? String(row.feeder1_initial_import_kwh) : '',
                  'Feeder1 Final import kWh': row.feeder1_final_import_kwh !== null && row.feeder1_final_import_kwh !== undefined ? String(row.feeder1_final_import_kwh) : '',
                  'B01Ff1 Net Import kWh': netImport
                };
                if (!loadedKeys.includes(dateKey)) {
                  loadedKeys.push(dateKey);
                }
              }
            });
          }
        }

        // Sort all dates (ascending)
        loadedKeys.sort();
        const finalKeys = [...loadedKeys];

        if (Object.keys(loadedData).length > 0) {
          // Filter out removed rows
          const { data: filteredData, rowKeys: filteredKeys } = filterRemovedRows(
            loadedData,
            finalKeys,
            'Meter Reading',
            siteCode,
            userIdForLoad
          );

          // Merge fresh data with current data to preserve unsaved rows.
          // Read via refs (not the `data`/`rowKeys` closure values) so this effect
          // doesn't need `data`/`rowKeys` in its dependency array — see refs above.
          const currentData = dataRef.current;
          const currentRowKeys = rowKeysRef.current;
          const mergedData = { ...currentData }; // Start with current data (includes unsaved rows)
          const mergedKeys: string[] = []; // Will build merged keys list

          // Track which keys are from database
          const dbKeysSet = new Set(filteredKeys);

          // First, add all rows from database (these are saved)
          filteredKeys.forEach(key => {
            if (filteredData[key]) {
              mergedData[key] = filteredData[key];
              mergedKeys.push(key);
            }
          });

          // Then, preserve all rows that exist in current data but not in database (unsaved rows)
          // Also preserve rows that match justAddedRowRef (for newly added rows)
          currentRowKeys.forEach(key => {
            const isNewlyAddedRow = justAddedRowRef.current && key === justAddedRowRef.current;

            if ((!dbKeysSet.has(key) || isNewlyAddedRow) && currentData[key]) {
              // This is an unsaved row (exists locally but not in DB), preserve it
              mergedData[key] = currentData[key];
              if (!mergedKeys.includes(key)) {
                mergedKeys.push(key);
              }
              MTR_DEBUG && console.log(`💾 [REAL-TIME] Preserving unsaved row: ${key}${isNewlyAddedRow ? ' (newly added)' : ''}`);
            }
          });

          // Sort merged keys (ascending by date)
          mergedKeys.sort();

          // Only update if data has changed (to avoid unnecessary re-renders)
          const currentDataSignature = JSON.stringify(Object.keys(currentData).sort());
          const newDataSignature = JSON.stringify(Object.keys(mergedData).sort());

          if (currentDataSignature !== newDataSignature ||
            JSON.stringify([...currentRowKeys].sort()) !== JSON.stringify(mergedKeys.sort())) {
            MTR_DEBUG && console.log(`🔄 [REAL-TIME] Refreshed data: ${mergedKeys.length} records (preserved ${currentRowKeys.length - filteredKeys.length} unsaved rows)`);
            setAllData(mergedData, mergedKeys);
          }
        }
      } catch (error) {
        console.error('Error refreshing Meter Reading data:', error);
      }
    };

    // Poll every 30 seconds for fresh data (only when tab is visible)
    const intervalId = isTabVisible ? setInterval(loadFreshData, 30000) : null;

    // Also refresh shortly after mount when visible
    const timeoutId = isTabVisible ? setTimeout(loadFreshData, 5000) : null; // First refresh after 5 seconds

    return () => {
      if (intervalId) clearInterval(intervalId);
      if (timeoutId) clearTimeout(timeoutId);
    };
    // `data`/`rowKeys` intentionally excluded — read via dataRef/rowKeysRef above so
    // this effect doesn't tear down and re-arm its 30s/5s timers on every keystroke.
  }, [isInitialLoad, site, siteCode, user, setAllData, isTabVisible]);

  /**
   * Auto-sort rows by date (ascending - oldest to newest, so newest appears at bottom)
   */
  useEffect(() => {
    if (isInitialLoad || rowKeys.length === 0) return;

    let timeoutId1: NodeJS.Timeout | null = null;
    let timeoutId2: NodeJS.Timeout | null = null;

    const sortRows = () => {
      if (isManuallySortingRef.current) return;

      const sortedKeys = [...rowKeys].sort();

      // Compare stringified arrays to avoid reference comparison issues
      const isAlreadySorted = JSON.stringify(sortedKeys) === JSON.stringify(rowKeys);

      if (!isAlreadySorted) {
        MTR_DEBUG && console.log('🔄 Auto-sorting rows by date', { current: rowKeys, expected: sortedKeys });
        isManuallySortingRef.current = true;
        timeoutId1 = setTimeout(() => {
          setRowKeysLocal(sortedKeys);
          setTableKey(prev => prev + 1);
          timeoutId2 = setTimeout(() => {
            isManuallySortingRef.current = false;
          }, 100);
        }, 0);
      }
    };

    // If manual sorting is in progress, wait for it to complete, then sort
    if (isManuallySortingRef.current) {
      const timeoutId = setTimeout(() => {
        sortRows();
      }, 150);
      return () => {
        clearTimeout(timeoutId);
        if (timeoutId1) clearTimeout(timeoutId1);
        if (timeoutId2) clearTimeout(timeoutId2);
      };
    }

    // Otherwise, sort immediately
    sortRows();

    return () => {
      if (timeoutId1) clearTimeout(timeoutId1);
      if (timeoutId2) clearTimeout(timeoutId2);
    };
    // `data`/`setAllData` were listed here but never read in this effect — they only
    // forced a re-sort-check on every keystroke (which changes `data`, not `rowKeys`).
  }, [rowKeys, isInitialLoad]);

  // Reset scroll flag when initial load starts or when table re-renders (so we can position when it completes)
  useEffect(() => {
    if (isInitialLoad) {
      hasScrolledToBottomRef.current = false;
      setIsTableVisible(false); // Hide table during initial load
    }
  }, [isInitialLoad]);

  // Reset scroll flag when tableKey changes (table re-rendered due to sorting, need to reposition)
  useEffect(() => {
    if (tableKey > 0 && !isInitialLoad && rowKeys.length > 0 && !isManuallySortingRef.current) {
      // Only reset if we're not in the middle of manual sorting
      hasScrolledToBottomRef.current = false;
      setIsTableVisible(false); // Hide table when it re-renders so we can reposition
    }
  }, [tableKey, isInitialLoad, rowKeys.length]);

  // Lazy loading: Detect scroll up and load more older data
  // Uses BOTH scroll + wheel events to handle cases where content doesn't fill viewport
  useEffect(() => {
    if (startDate || endDate) return;
    if (isInitialLoad || !containerRef.current) return;

    const container = containerRef.current;
    let scrollTimeout: NodeJS.Timeout;
    let wheelTimeout: NodeJS.Timeout;

    const triggerLoadMore = () => {
      if (!containerRef.current || isLoadingMoreRef.current || !hasMoreDataRef.current) return;
      loadMoreOlderData();
    };

    const handleScroll = () => {
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        if (!containerRef.current || isLoadingMoreRef.current || !hasMoreDataRef.current) return;
        const scrollTop = container.scrollTop;
        if (scrollTop <= SCROLL_TOP_LOAD_THRESHOLD) {
          triggerLoadMore();
        }
      }, 150);
    };

    // Wheel event fires even when content doesn't overflow (not scrollable)
    const handleWheel = (e: WheelEvent) => {
      if (e.deltaY >= 0) return; // Only react to scroll-up (deltaY < 0)
      if (isLoadingMoreRef.current || !hasMoreDataRef.current) return;
      const scrollTop = container.scrollTop;
      const isNotScrollable = container.scrollHeight <= container.clientHeight;
      if (scrollTop <= SCROLL_TOP_LOAD_THRESHOLD || isNotScrollable) {
        clearTimeout(wheelTimeout);
        wheelTimeout = setTimeout(() => triggerLoadMore(), 150);
      }
    };

    container.addEventListener('scroll', handleScroll, { passive: true });
    container.addEventListener('wheel', handleWheel, { passive: true });

    // Auto-fill: if content doesn't fill viewport after load, load more immediately
    const isNotScrollable = container.scrollHeight <= container.clientHeight;
    if (isNotScrollable && hasMoreDataRef.current && !isLoadingMoreRef.current && rowKeys.length > 0) {
      triggerLoadMore();
    }

    return () => {
      container.removeEventListener('scroll', handleScroll);
      container.removeEventListener('wheel', handleWheel);
      clearTimeout(scrollTimeout);
      clearTimeout(wheelTimeout);
    };
  }, [isInitialLoad, rowKeys.length, data, startDate, endDate]);

  // Set initial scroll position to bottom SYNCHRONOUSLY before browser paints
  // This ensures the sheet starts directly from bottom rows - user never sees top rows
  useLayoutEffect(() => {
    // Wait for data to be available, initial load to complete, and sorting to finish
    if (isInitialLoad || !containerRef.current || isManuallySortingRef.current) {
      hasScrolledToBottomRef.current = false;
      setIsTableVisible(false); // Hide table during initial load or sorting
      return;
    }

    // If no data, just show the empty table with headers
    if (rowKeys.length === 0) {
      hasScrolledToBottomRef.current = false;
      setIsTableVisible(true);
      return;
    }

    // If we've already set the position, don't do it again
    if (hasScrolledToBottomRef.current) return;

    const container = containerRef.current;

    // Check if rows are rendered in the DOM
    const tbody = container.querySelector('tbody');
    const actualRowCount = tbody ? tbody.querySelectorAll('tr').length : 0;
    const expectedRowCount = rowKeys.length;

    const scrollHeight = container.scrollHeight;
    const clientHeight = container.clientHeight;
    const maxScroll = scrollHeight - clientHeight;

    // CRITICAL: Check if rows are rendered (even if just 1 row, we want to position at bottom)
    const rowsRendered = actualRowCount > 0;
    const hasScrollableContent = maxScroll > 0 && scrollHeight > clientHeight;

    // If we have rows rendered, always position to bottom (even if not scrollable, position at 0 which is bottom for small content)
    if (rowsRendered) {
      // Disable smooth scrolling to prevent animation (set permanently)
      container.style.scrollBehavior = 'auto';

      // Set scroll position directly to bottom IMMEDIATELY (synchronously, before paint)
      // Use maxScroll if scrollable, otherwise 0 (which shows bottom for small content)
      const targetScroll = hasScrollableContent ? maxScroll : 0;
      container.scrollTop = targetScroll;

      // Force immediate update by reading scrollTop (triggers layout recalculation)
      const currentScroll = container.scrollTop;

      // Verify position was set correctly (allow some tolerance)
      if (Math.abs(currentScroll - targetScroll) < 1 || currentScroll >= targetScroll - 5 || (!hasScrollableContent && currentScroll === 0)) {
        hasScrolledToBottomRef.current = true;

        // Show table only after scroll is positioned correctly
        setIsTableVisible(true);

        MTR_DEBUG && console.log('⬇️ [Meter Reading] useLayoutEffect: Set position to bottom (sheet starts from bottom rows)', {
          scrollHeight,
          clientHeight,
          maxScroll,
          targetScroll,
          actualScrollTop: currentScroll,
          expectedRowCount,
          actualRowCount,
          rowsRendered,
          hasScrollableContent
        });
      } else {
        // If position wasn't set correctly, try again in next frame
        requestAnimationFrame(() => {
          if (containerRef.current && !hasScrolledToBottomRef.current) {
            const retryMaxScroll = containerRef.current.scrollHeight - containerRef.current.clientHeight;
            const retryTarget = retryMaxScroll > 0 ? retryMaxScroll : 0;
            containerRef.current.scrollTop = retryTarget;
            const retryScroll = containerRef.current.scrollTop;
            if (Math.abs(retryScroll - retryTarget) < 1 || retryScroll >= retryTarget - 5 || (!hasScrollableContent && retryScroll === 0)) {
              hasScrolledToBottomRef.current = true;
              setIsTableVisible(true);
            }
          }
        });
      }
    }
  }, [isInitialLoad, rowKeys.length, tableKey]);

  // Fallback useEffect for cases where useLayoutEffect runs before rows are fully rendered
  useEffect(() => {
    // Wait for data to be available and initial load to complete
    if (isInitialLoad || rowKeys.length === 0 || !containerRef.current) {
      return;
    }

    // If we've already set the position, don't do it again
    if (hasScrolledToBottomRef.current) return;

    const container = containerRef.current;
    const expectedRowCount = rowKeys.length;

    const setPositionToBottom = () => {
      if (!containerRef.current || hasScrolledToBottomRef.current) return;

      const container = containerRef.current;

      // Check if all rows are actually rendered in the DOM
      const tbody = container.querySelector('tbody');
      const actualRowCount = tbody ? tbody.querySelectorAll('tr').length : 0;

      const scrollHeight = container.scrollHeight;
      const clientHeight = container.clientHeight;
      const maxScroll = scrollHeight - clientHeight;

      // Check if rows are rendered
      const rowsRendered = actualRowCount > 0;
      const hasScrollableContent = maxScroll > 0 && scrollHeight > clientHeight;

      if (rowsRendered) {
        // Disable smooth scrolling to prevent animation (set permanently)
        container.style.scrollBehavior = 'auto';

        // Set scroll position directly to bottom immediately (no animation)
        // Use maxScroll if scrollable, otherwise 0 (which shows bottom for small content)
        const targetScroll = hasScrollableContent ? maxScroll : 0;
        container.scrollTop = targetScroll;

        // Force immediate update by reading scrollTop
        const currentScroll = container.scrollTop;

        // Verify position was set correctly (allow some tolerance)
        if (Math.abs(currentScroll - targetScroll) < 1 || currentScroll >= targetScroll - 5 || (!hasScrollableContent && currentScroll === 0)) {
          hasScrolledToBottomRef.current = true;

          // Show table only after scroll is positioned correctly
          setIsTableVisible(true);

          MTR_DEBUG && console.log('⬇️ [Meter Reading] useEffect fallback: Set position to bottom (sheet starts from bottom rows)', {
            scrollHeight,
            clientHeight,
            maxScroll,
            targetScroll,
            actualScrollTop: container.scrollTop,
            expectedRowCount,
            actualRowCount,
            hasScrollableContent
          });
        }
      }
    };

    // Use MutationObserver to watch for DOM changes and set position when table updates
    const observer = new MutationObserver(() => {
      if (!hasScrolledToBottomRef.current && containerRef.current) {
        setPositionToBottom();
      }
    });

    if (container) {
      observer.observe(container, {
        childList: true,
        subtree: true,
        attributes: false,
        characterData: false
      });
    }

    // Try immediately
    setPositionToBottom();

    // Try with requestAnimationFrame
    requestAnimationFrame(() => {
      setPositionToBottom();
      requestAnimationFrame(() => {
        setPositionToBottom();
      });
    });

    // Try after various delays to catch different render stages
    const timeouts = [
      setTimeout(setPositionToBottom, 0),
      setTimeout(setPositionToBottom, 50),
      setTimeout(setPositionToBottom, 100),
      setTimeout(setPositionToBottom, 200),
      setTimeout(setPositionToBottom, 300),
      setTimeout(setPositionToBottom, 500),
      setTimeout(setPositionToBottom, 800)
    ];

    return () => {
      observer.disconnect();
      timeouts.forEach(clearTimeout);
    };
  }, [isInitialLoad, rowKeys.length, tableKey]);

  /**
   * Auto-calculate Net kWh when Initial or Final Export values change
   */
  useEffect(() => {
    if (isInitialLoad) return;

    const updatedData = { ...data };
    let wasUpdated = false;

    rowKeys.forEach(rowKey => {
      const row = updatedData[rowKey];
      if (!row) return;

      const initial = parseFloat(row['Feeder1 Initial Export']) || 0;
      const final = parseFloat(row['Feeder1 Final Export']) || 0;

      // Calculate net kWh (Final - Initial)
      let calculatedNet = '';

      if (row['Feeder1 Initial Export'] && row['Feeder1 Final Export']) {
        const netValue = final - initial;
        calculatedNet = netValue.toFixed(2);
      }

      // Only update if the calculated value is different from current
      if (row['Feeder1 Net kWh'] !== calculatedNet) {
        updatedData[rowKey] = {
          ...row,
          'Feeder1 Net kWh': calculatedNet
        };
        wasUpdated = true;
      }
    });

    if (wasUpdated) {
      setAllData(updatedData, rowKeys);
      MTR_DEBUG && console.log('🧮 Auto-calculated Net kWh values');
    }
  }, [data, rowKeys, isInitialLoad, setAllData]);

  /**
   * Load existing data for a specific date from database
   */
  const loadDataForDate = async (date: string): Promise<any | null> => {
    try {
      const userResponse = await backendApi.getCurrentUser();
      if (!userResponse.success || !userResponse.user?.id) {
        return null;
      }

      const backendUser = userResponse.user;
      const resolvedUserId = resolveUserFilterId(user, backendUser);
      const userIdForLoad = (resolvedUserId ?? Number(backendUser.id)) || 0;
      const isAdmin = shouldLoadAllRecords(user, backendUser);

      let existingDataForDate: any = null;

      if (isAdmin) {
        // Admin: Use get-dgr endpoint
        try {
          const API_BASE_URL = 'http://localhost:8000/api/v1';
          const token = sessionStorage.getItem('access_token');
          const response = await fetch(`${API_BASE_URL}/get-dgr/${siteCode}/?sheet=Meter Reading`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (response.ok) {
            const payload = await response.json();
            const meterData = payload?.data?.meter || [];
            existingDataForDate = meterData.find((row: any) => row.entry_date === date);
          }
        } catch (error) {
          MTR_DEBUG && console.warn('Error loading data for date via get-dgr:', error);
        }
      } else {
        // Regular user: Load their data and find by date
        const loadResult = await meterReadingService.loadByUser(userIdForLoad);
        if (loadResult.ok && loadResult.data) {
          existingDataForDate = loadResult.data.find((row: any) => row.entry_date === date);
        }
      }

      return existingDataForDate || null;
    } catch (error) {
      console.error('Error loading data for date:', error);
      return null;
    }
  };

  /**
   * FIXED: Enhanced updateCell with correct signature for UniversalTable
   * This function matches the expected signature: (rowKey: string, columnKey: string, value: any) => void
   * Also handles date column changes to check database and load existing data
   */
  const updateCellWithCalculation = async (rowKey: string, columnKey: string, value: any) => {
    // If this is a Date column change, check database and load data if exists
    if (columnKey === 'Date' && value && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
      const rowIndex = rowKeys.indexOf(rowKey);
      if (rowIndex >= 0) {
        // First update the cell value
        const updatedData = { ...data };
        if (!updatedData[rowKey]) {
          updatedData[rowKey] = createEmptyRow(rowKey);
        }
        updatedData[rowKey][columnKey] = value;
        setAllData(updatedData, rowKeys);

        // Check if data exists in database for this date
        const existingData = await loadDataForDate(value);

        if (existingData) {
          // Data exists - populate the row with existing data
          MTR_DEBUG && console.log(`📥 Found existing data for date ${value}, loading...`);

          // Handle Net Import field
          const netImport = existingData.net_import !== null && existingData.net_import !== undefined
            ? String(existingData.net_import)
            : (existingData['Net Import'] !== null && existingData['Net Import'] !== undefined
              ? String(existingData['Net Import'])
              : '');

          // Map backend data to frontend format
          const mappedRow: any = {
            id: existingData.id || existingData.pk || undefined,
            Date: value,
            'Feeder1 Initial Export': existingData.feeder1_initial_export !== null && existingData.feeder1_initial_export !== undefined ? String(existingData.feeder1_initial_export) : '',
            'Feeder1 Final Export': existingData.feeder1_final_export !== null && existingData.feeder1_final_export !== undefined ? String(existingData.feeder1_final_export) : '',
            'Feeder1 Net kWh': existingData.feeder1_net_kwh !== null && existingData.feeder1_net_kwh !== undefined ? String(existingData.feeder1_net_kwh) : '',
            'Inverter Generation 600V': existingData.inverter_generation_600v !== null && existingData.inverter_generation_600v !== undefined ? String(existingData.inverter_generation_600v) : '',
            'Feeder1 Initial import kWh': existingData.feeder1_initial_import_kwh !== null && existingData.feeder1_initial_import_kwh !== undefined ? String(existingData.feeder1_initial_import_kwh) : '',
            'Feeder1 Final import kWh': existingData.feeder1_final_import_kwh !== null && existingData.feeder1_final_import_kwh !== undefined ? String(existingData.feeder1_final_import_kwh) : '',
            'B01Ff1 Net Import kWh': netImport
          };

          // Update the row data with loaded data
          const updatedDataWithLoaded = { ...updatedData };
          updatedDataWithLoaded[rowKey] = mappedRow;
          setAllData(updatedDataWithLoaded, rowKeys);

          setStatus({
            type: 'success',
            message: `✅ Loaded existing data for ${value}`
          });
        } else {
          // No data exists - keep as empty row
          MTR_DEBUG && console.log(`📝 No existing data for date ${value}, keeping as empty row`);
        }

        // Then update the row key (which will trigger sorting)
        // Use a small delay to ensure data loading completes first
        setTimeout(() => {
          handleRowKeyChange(rowIndex, value);
        }, 100);
        return;
      }
    }

    // First, update the cell normally using the correct signature
    const updatedData = { ...data };
    if (!updatedData[rowKey]) {
      updatedData[rowKey] = createEmptyRow(rowKey);
    }
    updatedData[rowKey][columnKey] = value;
    setAllData(updatedData, rowKeys);

    // Then check if we need to calculate Net kWh
    if (columnKey === 'Feeder1 Initial Export' || columnKey === 'Feeder1 Final Export') {
      // Use requestAnimationFrame for better performance and automatic cleanup
      const timeoutId = setTimeout(() => {
        const currentRow = updatedData[rowKey];
        if (!currentRow) return;

        const initial = parseFloat(currentRow['Feeder1 Initial Export']) || 0;
        const final = parseFloat(currentRow['Feeder1 Final Export']) || 0;

        if (currentRow['Feeder1 Initial Export'] && currentRow['Feeder1 Final Export']) {
          const netValue = final - initial;
          const calculatedNet = netValue.toFixed(2);

          if (currentRow['Feeder1 Net kWh'] !== calculatedNet) {
            const updatedDataWithCalc = { ...updatedData };
            updatedDataWithCalc[rowKey] = {
              ...updatedDataWithCalc[rowKey],
              'Feeder1 Net kWh': calculatedNet
            };
            setAllData(updatedDataWithCalc, rowKeys);
            MTR_DEBUG && console.log(`🧮 Auto-calculated Net kWh for ${rowKey}: ${calculatedNet}`);
          }
        }
      }, 50); // Small delay to ensure the original update completes

      // Note: This timeout is in an event handler, not useEffect, so cleanup isn't critical
      // but we can store it in a ref if needed for cancellation
    }
  };

  /**
   * Add new row with auto-incremented date
   * Also checks database for existing data for this date and loads it if found
   */
  const handleAddRow = async () => {
    let newDate: string;

    if (rowKeys.length > 0) {
      // Get latest date and add 1 day
      const sortedDates = [...rowKeys].sort();
      const latestDate = sortedDates[sortedDates.length - 1];
      const nextDay = new Date(latestDate);
      nextDay.setDate(nextDay.getDate() + 1);
      newDate = nextDay.toISOString().split('T')[0];
    } else {
      newDate = new Date().toISOString().split('T')[0];
    }

    // Check if date already exists
    if (rowKeys.includes(newDate)) {
      setStatus({
        type: 'warning',
        message: `⚠️ Date ${newDate} already exists. Please edit existing row or delete it.`
      });

      const existingIndex = rowKeys.indexOf(newDate);
      setFocusedCell({ rowIndex: existingIndex, colIndex: 0 });
      setTimeout(() => scrollToCell(existingIndex, 0), 100);
      return;
    }

    MTR_DEBUG && console.log(`➕ Adding new row: ${newDate}`);

    // Mark this row as just added to prevent real-time refresh from overwriting it
    justAddedRowRef.current = newDate;

    // Check if data exists in database for this date
    try {
      const userResponse = await backendApi.getCurrentUser();
      if (userResponse.success && userResponse.user?.id) {
        const backendUser = userResponse.user;
        const resolvedUserId = resolveUserFilterId(user, backendUser);
        const userIdForLoad = (resolvedUserId ?? Number(backendUser.id)) || 0;
        const isAdmin = shouldLoadAllRecords(user, backendUser);

        // Load data for this specific date
        let existingDataForDate: any = null;

        if (isAdmin) {
          // Admin: Use get-dgr endpoint for the specific date
          try {
            const API_BASE_URL = 'http://localhost:8000/api/v1';
            const token = sessionStorage.getItem('access_token');
            const response = await fetch(`${API_BASE_URL}/get-dgr/${siteCode}/?sheet=Meter Reading`, {
              headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
              const payload = await response.json();
              const meterData = payload?.data?.meter || [];
              // Find data for this date (Meter Reading has one row per date)
              existingDataForDate = meterData.find((row: any) => row.entry_date === newDate);
            }
          } catch (error) {
            MTR_DEBUG && console.warn('Error loading data for date via get-dgr:', error);
          }
        } else {
          // Regular user: Load their data and find by date
          const loadResult = await meterReadingService.loadByUser(userIdForLoad);
          if (loadResult.ok && loadResult.data) {
            existingDataForDate = loadResult.data.find((row: any) => row.entry_date === newDate);
          }
        }

        // If data exists, populate row with that data
        if (existingDataForDate) {
          MTR_DEBUG && console.log(`📥 Found existing record for date ${newDate}, loading data...`);

          // Handle Net Import field
          const netImport = existingDataForDate.net_import !== null && existingDataForDate.net_import !== undefined
            ? String(existingDataForDate.net_import)
            : (existingDataForDate['Net Import'] !== null && existingDataForDate['Net Import'] !== undefined
              ? String(existingDataForDate['Net Import'])
              : '');

          const updatedData = {
            ...data,
            [newDate]: {
              id: existingDataForDate.id || existingDataForDate.pk || undefined,
              Date: newDate,
              'Feeder1 Initial Export': existingDataForDate.feeder1_initial_export !== null && existingDataForDate.feeder1_initial_export !== undefined ? String(existingDataForDate.feeder1_initial_export) : '',
              'Feeder1 Final Export': existingDataForDate.feeder1_final_export !== null && existingDataForDate.feeder1_final_export !== undefined ? String(existingDataForDate.feeder1_final_export) : '',
              'Feeder1 Net kWh': existingDataForDate.feeder1_net_kwh !== null && existingDataForDate.feeder1_net_kwh !== undefined ? String(existingDataForDate.feeder1_net_kwh) : '',
              'Inverter Generation 600V': existingDataForDate.inverter_generation_600v !== null && existingDataForDate.inverter_generation_600v !== undefined ? String(existingDataForDate.inverter_generation_600v) : '',
              'Feeder1 Initial import kWh': existingDataForDate.feeder1_initial_import_kwh !== null && existingDataForDate.feeder1_initial_import_kwh !== undefined ? String(existingDataForDate.feeder1_initial_import_kwh) : '',
              'Feeder1 Final import kWh': existingDataForDate.feeder1_final_import_kwh !== null && existingDataForDate.feeder1_final_import_kwh !== undefined ? String(existingDataForDate.feeder1_final_import_kwh) : '',
              'B01Ff1 Net Import kWh': netImport
            }
          };

          // Add new date and sort all dates
          const updatedKeys = [...rowKeys, newDate].sort();

          // Update local state
          setRowKeysLocal(updatedKeys);
          setAllData(updatedData, updatedKeys);

          setStatus({
            type: 'success',
            message: `✅ Loaded existing data for ${newDate}`
          });

          // Focus on new row
          setTimeout(() => {
            const newIndex = updatedKeys.indexOf(newDate);
            setFocusedCell({ rowIndex: newIndex, colIndex: 1 });
            scrollToCell(newIndex, 1);
          }, 150);

          // Clear the flag after a delay
          setTimeout(() => {
            justAddedRowRef.current = null;
          }, 10000);

          return;
        }
      }
    } catch (error) {
      MTR_DEBUG && console.warn('Error checking database for existing data:', error);
      // Continue with creating empty row if check fails
    }

    // No data found in database, create empty row
    MTR_DEBUG && console.log(`📝 No existing data found for ${newDate}, creating empty row`);

    // Create new row
    const updatedData = {
      ...data,
      [newDate]: createEmptyRow(newDate)
    };

    // Add new date and sort all dates
    const updatedKeys = [...rowKeys, newDate].sort();

    // Update local state immediately
    setRowKeysLocal(updatedKeys);
    setAllData(updatedData, updatedKeys);

    // Clear the flag after a delay to allow real-time refresh to resume
    setTimeout(() => {
      justAddedRowRef.current = null;
    }, 10000); // 10 seconds should be enough for user to start editing

    // Focus on new row
    setTimeout(() => {
      const newIndex = updatedKeys.indexOf(newDate);
      setFocusedCell({ rowIndex: newIndex, colIndex: 1 });
      scrollToCell(newIndex, 1);
    }, 150);
  };

  /**
   * FIXED: Handle row key (date) change with correct string parameter
   */
  const handleRowKeyChange = (index: number, newKey: string) => {
    const oldKey = rowKeys[index];
    const today = new Date().toISOString().split('T')[0];

    // Validate date format
    if (!/^\d{4}-\d{2}-\d{2}$/.test(newKey)) {
      setStatus({
        type: 'error',
        message: '❌ Invalid date format. Use YYYY-MM-DD.'
      });
      return;
    }

    // Check if new date already exists
    if (rowKeys.includes(newKey) && newKey !== oldKey) {
      setStatus({
        type: 'error',
        message: `❌ Date ${newKey} already exists.`
      });
      return;
    }

    // Update date
    const updatedData = { ...data };
    // If data already exists for newKey (e.g., loaded from database), preserve it
    // Otherwise, copy from oldKey and update the Date field
    if (updatedData[newKey]) {
      // Data already exists for new key (likely loaded from database) - just ensure Date is correct
      updatedData[newKey] = { ...updatedData[newKey], Date: newKey };
    } else {
      // No data for new key - copy from old key
      updatedData[newKey] = { ...updatedData[oldKey], Date: newKey };
    }
    delete updatedData[oldKey];

    // Replace the old key with new key in the keys array and sort
    const keysAfterChange = rowKeys.map(k => k === oldKey ? newKey : k);
    const updatedKeys = [...keysAfterChange].sort();

    MTR_DEBUG && console.log('🔄 Reordering rows after date change:', {
      oldKey,
      newKey,
      oldIndex: index,
      beforeSort: keysAfterChange,
      afterSort: updatedKeys,
      newIndex: updatedKeys.indexOf(newKey)
    });

    isManuallySortingRef.current = true;

    // Create completely new references to ensure React detects the change
    const finalKeys = Array.from(updatedKeys); // Use Array.from to create new reference
    const finalData = { ...updatedData }; // Create new object reference

    // Update state using setTimeout to avoid flushSync during render
    setTimeout(() => {
      setRowKeysLocal(finalKeys);
      setTableKey(prev => prev + 1);
      setAllData(finalData, finalKeys);
    }, 0);

    // Update focused cell to the new position after reordering
    const newIndex = updatedKeys.indexOf(newKey);
    if (newIndex >= 0 && newIndex !== index) {
      setTimeout(() => {
        setFocusedCell({ rowIndex: newIndex, colIndex: focusedCell.colIndex });
        scrollToCell(newIndex, focusedCell.colIndex);
      }, 50);
    }

    // Reset flag after state update completes
    setTimeout(() => {
      isManuallySortingRef.current = false;
    }, 100);

    MTR_DEBUG && console.log(`✅ Date changed successfully - row moved from index ${index} to ${updatedKeys.indexOf(newKey)}`);
  };

  /**
   * Handle row deletion - shows dialog for permanent vs temporary removal
   */
  const handleRemoveRow = (index: number) => {
    const keyToRemove = rowKeys[index];
    setRowToDelete({ index, key: keyToRemove });
    setShowDeleteDialog(true);
  };

  /**
   * Handle permanent deletion - delete from database
   */
  const handlePermanentDelete = async () => {
    if (!rowToDelete) return;

    const keyToRemove = rowToDelete.key;
    const entryDate = keyToRemove; // For Meter Reading, key is the date

    try {
      setIsSaving(true);
      const deleteResult = await meterReadingService.delete(entryDate);

      if (deleteResult.ok) {
        // Remove from removed rows list if it was there
        backendApi.getCurrentUser().then(response => {
          if (response.success && response.user?.id) {
            const backendUser = response.user;
            const resolvedUserId = resolveUserFilterId(user, backendUser);
            const userIdForStore = resolvedUserId ?? Number(backendUser.id);
            removeRemovedRow('Meter Reading', siteCode, keyToRemove, userIdForStore);
          } else {
            removeRemovedRow('Meter Reading', siteCode, keyToRemove);
          }
        }).catch(() => {
          removeRemovedRow('Meter Reading', siteCode, keyToRemove);
        });

        // Remove the row from frontend state
        const updatedData = { ...data };
        delete updatedData[keyToRemove];
        const updatedKeys = rowKeys.filter(k => k !== keyToRemove).sort();

        setAllData(updatedData, updatedKeys);

        setStatus({
          type: 'success',
          message: `Permanently deleted ${keyToRemove} from database`
        });

        // If no rows left, add a new one
        if (updatedKeys.length === 0) {
          setTimeout(() => {
            const today = new Date().toISOString().split('T')[0];
            const initialData = {};
            setAllData(initialData, [today]);
            MTR_DEBUG && console.log(`➕ Added new row after last remove: ${today}`);

            setStatus({
              type: 'info',
              message: `ℹ️ Added new row with today's date: ${today}`
            });
          }, 100);
        } else {
          // Focus on the previous row or the first row if we removed the first one
          const newFocusIndex = Math.max(0, rowToDelete.index - 1);
          setTimeout(() => {
            setFocusedCell({ rowIndex: newFocusIndex, colIndex: 0 });
            scrollToCell(newFocusIndex, 0);
          }, 100);
        }
      } else {
        setStatus({
          type: 'error',
          message: `Failed to delete: ${deleteResult.error || 'Unknown error'}`
        });
      }
    } catch (error: any) {
      setStatus({
        type: 'error',
        message: `Failed to delete: ${error.message || 'Unknown error'}`
      });
    } finally {
      setIsSaving(false);
      setRowToDelete(null);
    }
  };

  /**
   * Handle temporary removal - hide until page refresh
   */
  const handleTemporaryRemove = () => {
    if (!rowToDelete) return;

    const keyToRemove = rowToDelete.key;
    const index = rowToDelete.index;

    // Add to removed rows list (persists across sheet switches)
    backendApi.getCurrentUser().then(response => {
      if (response.success && response.user?.id) {
        const backendUser = response.user;
        const resolvedUserId = resolveUserFilterId(user, backendUser);
        const userIdForStore = resolvedUserId ?? Number(backendUser.id);
        addRemovedRow('Meter Reading', siteCode, keyToRemove, userIdForStore);
      } else {
        addRemovedRow('Meter Reading', siteCode, keyToRemove);
      }
    }).catch(() => {
      addRemovedRow('Meter Reading', siteCode, keyToRemove);
    });

    // Remove the row from frontend state only
    const updatedData = { ...data };
    delete updatedData[keyToRemove];
    const updatedKeys = rowKeys.filter(k => k !== keyToRemove).sort();

    setAllData(updatedData, updatedKeys);

    setStatus({
      type: 'info',
      message: `Temporarily hidden ${keyToRemove} (will reappear after page refresh)`
    });

    // If no rows left, add a new one
    if (updatedKeys.length === 0) {
      setTimeout(() => {
        const today = new Date().toISOString().split('T')[0];
        const initialData = {};
        setAllData(initialData, [today]);
        MTR_DEBUG && console.log(`➕ Added new row after last remove: ${today}`);

        setStatus({
          type: 'info',
          message: `ℹ️ Added new row with today's date: ${today}`
        });
      }, 100);
    } else {
      // Focus on the previous row or the first row if we removed the first one
      const newFocusIndex = Math.max(0, index - 1);
      setTimeout(() => {
        setFocusedCell({ rowIndex: newFocusIndex, colIndex: 0 });
        scrollToCell(newFocusIndex, 0);
      }, 100);
    }

    setRowToDelete(null);
  };

  /**
   * Save all meter readings to backend
   */
  const save = async () => {
    setIsExporting(true);

    try {
      const entriesToSave: MeterReadingDataRow[] = [];

      MTR_DEBUG && console.log(`💾 Preparing to save ${rowKeys.length} rows...`);

      rowKeys.forEach(key => {
        const rowData = data[key];

        // Calculate Feeder1 Net kWh = Final Export - Initial Export
        let feeder1NetKwh: number | null = null;
        if (rowData['Feeder1 Net kWh'] && rowData['Feeder1 Net kWh'].toString().trim() !== '') {
          const parsed = parseFloat(rowData['Feeder1 Net kWh'].toString().trim());
          feeder1NetKwh = isNaN(parsed) ? null : parsed;
        } else {
          const initialExport = rowData['Feeder1 Initial Export'] ? parseFloat(rowData['Feeder1 Initial Export'].toString().trim()) : null;
          const finalExport = rowData['Feeder1 Final Export'] ? parseFloat(rowData['Feeder1 Final Export'].toString().trim()) : null;
          if (initialExport !== null && finalExport !== null && !isNaN(initialExport) && !isNaN(finalExport)) {
            feeder1NetKwh = parseFloat((finalExport - initialExport).toFixed(3));
          }
        }

        // Calculate Net Import = Final Import - Initial Import
        let netImport: number | null = null;
        if (rowData['B01Ff1 Net Import kWh'] && rowData['B01Ff1 Net Import kWh'].toString().trim() !== '') {
          const parsed = parseFloat(rowData['B01Ff1 Net Import kWh'].toString().trim());
          netImport = isNaN(parsed) ? null : parsed;
        } else {
          const initialImport = rowData['Feeder1 Initial import kWh'] ? parseFloat(rowData['Feeder1 Initial import kWh'].toString().trim()) : null;
          const finalImport = rowData['Feeder1 Final import kWh'] ? parseFloat(rowData['Feeder1 Final import kWh'].toString().trim()) : null;
          if (initialImport !== null && finalImport !== null && !isNaN(initialImport) && !isNaN(finalImport)) {
            netImport = parseFloat((finalImport - initialImport).toFixed(3));
          }
        }

        MTR_DEBUG && console.log(`🧮 Calculated values for ${key}:`, {
          feeder1NetKwh,
          netImport
        });

        // Map UI column keys to backend field names
        const entry: MeterReadingDataRow = {
          id: rowData.id && typeof rowData.id === 'number' ? rowData.id : undefined, // Include ID if it exists
          entry_date: key,
          site_code: siteCode, // Add site_code for backend
          feeder1_initial_export: rowData['Feeder1 Initial Export'] && rowData['Feeder1 Initial Export'].toString().trim() !== '' ? parseFloat(rowData['Feeder1 Initial Export'].toString().trim()) : null,
          feeder1_final_export: rowData['Feeder1 Final Export'] && rowData['Feeder1 Final Export'].toString().trim() !== '' ? parseFloat(rowData['Feeder1 Final Export'].toString().trim()) : null,
          feeder1_net_kwh: feeder1NetKwh,
          inverter_generation_600v: rowData['Inverter Generation 600V'] && rowData['Inverter Generation 600V'].toString().trim() !== '' ? parseFloat(rowData['Inverter Generation 600V'].toString().trim()) : null,
          feeder1_initial_import_kwh: rowData['Feeder1 Initial import kWh'] && rowData['Feeder1 Initial import kWh'].toString().trim() !== '' ? parseFloat(rowData['Feeder1 Initial import kWh'].toString().trim()) : null,
          feeder1_final_import_kwh: rowData['Feeder1 Final import kWh'] && rowData['Feeder1 Final import kWh'].toString().trim() !== '' ? parseFloat(rowData['Feeder1 Final import kWh'].toString().trim()) : null,
          'Net Import': denormalizeNetImport(netImport)
        };

        MTR_DEBUG && console.log(`📦 Prepared entry for ${key}:`, JSON.stringify(entry, null, 2));

        // Only save if at least one field has data
        const hasData = Object.entries(entry).some(([k, val]) =>
          k !== 'entry_date' && val !== null && val !== undefined
        );

        // Only save new rows (rows without ID) - skip rows that are already in database
        const isNewRow = !entry.id || typeof entry.id !== 'number';

        if (hasData && isNewRow) {
          entriesToSave.push(entry);
          MTR_DEBUG && console.log(`✓ Row ${key} has data and is new, will be saved`);
        } else if (hasData && !isNewRow) {
          MTR_DEBUG && console.log(`⊘ Row ${key} already exists in database (ID: ${entry.id}), skipping save`);
        } else {
          MTR_DEBUG && console.log(`⊘ Row ${key} is empty, skipping`);
        }
      });

      if (entriesToSave.length === 0) {
        setStatus({
          type: 'warning',
          message: '⚠️ No data to save. Please enter some values.'
        });
        setIsExporting(false);
        return;
      }

      MTR_DEBUG && console.log(`💾 Saving ${entriesToSave.length} entries...`);

      let successCount = 0;
      let errorCount = 0;

      for (const entry of entriesToSave) {
        // Only save new entries (entries without ID) - entries with ID are already filtered out above
        // Remove ID from entry before creating (backend will assign new ID)
        const { id, ...entryWithoutId } = entry;
        const result = await meterReadingService.save(entryWithoutId);

        if (result.ok) {
          successCount++;
          MTR_DEBUG && console.log(`✅ Saved: ${entry.entry_date}`);
          // Clear the justAddedRowRef if this was the newly added row
          if (justAddedRowRef.current === entry.entry_date) {
            justAddedRowRef.current = null;
            MTR_DEBUG && console.log(`✅ Cleared justAddedRowRef after saving: ${entry.entry_date}`);
          }
        } else {
          errorCount++;
          console.error(`❌ Failed: ${entry.entry_date} - ${result.error}`);
        }
      }

      // Clear unsaved data from localStorage after successful save
      if (successCount > 0) {
        try {
          localStorage.removeItem(persistentStorageKey);
        } catch (error) {
          MTR_DEBUG && console.warn('Failed to clear saved data:', error);
        }
      }

      if (errorCount === 0) {
        setStatus({
          type: 'success',
          message: `✅ Successfully saved ${successCount} record${successCount > 1 ? 's' : ''}!`
        });
      } else {
        setStatus({
          type: 'warning',
          message: `⚠️ Saved ${successCount} record${successCount > 1 ? 's' : ''}, ${errorCount} failed.`
        });
      }

      // Reload saved data - use get-dgr for admins (all data), loadByUser for regular users
      if (successCount > 0) {
        // Get current user ID
        const userResponse = await backendApi.getCurrentUser();
        if (userResponse.success && userResponse.user?.id) {
          const backendUser = userResponse.user;
          const resolvedUserId = resolveUserFilterId(user, backendUser);
          const loadUserId = (resolvedUserId ?? Number(backendUser.id)) || 0;
          const isAdmin = shouldLoadAllRecords(user, backendUser);
          const savedDates = entriesToSave.map(e => e.entry_date);

          let loadedData: any = {};
          let loadedKeys: string[] = [];

          if (isAdmin) {
            // Admin: Load all data for the site using get-dgr endpoint
            try {
              const API_BASE_URL = 'http://localhost:8000/api/v1';
              const token = sessionStorage.getItem('access_token');
              const response = await fetch(`${API_BASE_URL}/get-dgr/${siteCode}/?sheet=Meter Reading`, {
                headers: { 'Authorization': `Bearer ${token}` }
              });
              if (response.ok) {
                const payload = await response.json();
                const meterData = payload?.data?.meter || [];

                meterData.forEach((row: any) => {
                  const dateKey = row.entry_date;
                  if (dateKey) {
                    loadedData[dateKey] = {
                      id: row.id || row.pk || undefined, // Store ID for updates
                      Date: dateKey,
                      'Feeder1 Initial Export': row.feeder1_initial_export !== null && row.feeder1_initial_export !== undefined ? String(row.feeder1_initial_export) : '',
                      'Feeder1 Final Export': row.feeder1_final_export !== null && row.feeder1_final_export !== undefined ? String(row.feeder1_final_export) : '',
                      'Feeder1 Net kWh': row.feeder1_net_kwh !== null && row.feeder1_net_kwh !== undefined ? String(row.feeder1_net_kwh) : '',
                      'Inverter Generation 600V': row.inverter_generation_600v !== null && row.inverter_generation_600v !== undefined ? String(row.inverter_generation_600v) : '',
                      'Feeder1 Initial import kWh': row.feeder1_initial_import_kwh !== null && row.feeder1_initial_import_kwh !== undefined ? String(row.feeder1_initial_import_kwh) : '',
                      'Feeder1 Final import kWh': row.feeder1_final_import_kwh !== null && row.feeder1_final_import_kwh !== undefined ? String(row.feeder1_final_import_kwh) : '',
                      'B01Ff1 Net Import kWh': (row['Net Import'] !== null && row['Net Import'] !== undefined ? String(row['Net Import']) : (row.net_import !== null && row.net_import !== undefined ? String(row.net_import) : ''))
                    };
                    if (!loadedKeys.includes(dateKey)) {
                      loadedKeys.push(dateKey);
                    }
                  }
                });
              }
            } catch (error) {
              console.error('Error loading all data via get-dgr:', error);
              // Fallback to loadByUser if get-dgr fails
              const loadResult = await meterReadingService.loadByUser(loadUserId);
              if (loadResult.ok && loadResult.data && loadResult.data.length > 0) {
                const savedData = loadResult.data.filter((row: any) =>
                  savedDates.includes(row.entry_date)
                );

                savedData.forEach((row: any) => {
                  const dateKey = row.entry_date;
                  if (dateKey) {
                    loadedData[dateKey] = {
                      id: row.id || row.pk || undefined, // Store ID for updates
                      Date: dateKey,
                      'Feeder1 Initial Export': row.feeder1_initial_export !== null && row.feeder1_initial_export !== undefined ? String(row.feeder1_initial_export) : '',
                      'Feeder1 Final Export': row.feeder1_final_export !== null && row.feeder1_final_export !== undefined ? String(row.feeder1_final_export) : '',
                      'Feeder1 Net kWh': row.feeder1_net_kwh !== null && row.feeder1_net_kwh !== undefined ? String(row.feeder1_net_kwh) : '',
                      'Inverter Generation 600V': row.inverter_generation_600v !== null && row.inverter_generation_600v !== undefined ? String(row.inverter_generation_600v) : '',
                      'Feeder1 Initial import kWh': row.feeder1_initial_import_kwh !== null && row.feeder1_initial_import_kwh !== undefined ? String(row.feeder1_initial_import_kwh) : '',
                      'Feeder1 Final import kWh': row.feeder1_final_import_kwh !== null && row.feeder1_final_import_kwh !== undefined ? String(row.feeder1_final_import_kwh) : '',
                      'B01Ff1 Net Import kWh': (row['Net Import'] !== null && row['Net Import'] !== undefined ? String(row['Net Import']) : (row.net_import !== null && row.net_import !== undefined ? String(row.net_import) : ''))
                    };
                    if (!loadedKeys.includes(dateKey)) {
                      loadedKeys.push(dateKey);
                    }
                  }
                });
              }
            }
          } else {
            // Regular user: Load only their data
            const loadResult = await meterReadingService.loadByUser(loadUserId);
            if (loadResult.ok && loadResult.data && loadResult.data.length > 0) {
              // Filter to only include the dates we just saved
              const savedData = loadResult.data.filter((row: any) =>
                savedDates.includes(row.entry_date)
              );

              savedData.forEach((row: any) => {
                const dateKey = row.entry_date;
                if (dateKey) {
                  loadedData[dateKey] = {
                    id: row.id || row.pk || undefined, // Store ID for updates
                    Date: dateKey,
                    'Feeder1 Initial Export': row.feeder1_initial_export !== null && row.feeder1_initial_export !== undefined ? String(row.feeder1_initial_export) : '',
                    'Feeder1 Final Export': row.feeder1_final_export !== null && row.feeder1_final_export !== undefined ? String(row.feeder1_final_export) : '',
                    'Feeder1 Net kWh': row.feeder1_net_kwh !== null && row.feeder1_net_kwh !== undefined ? String(row.feeder1_net_kwh) : '',
                    'Inverter Generation 600V': row.inverter_generation_600v !== null && row.inverter_generation_600v !== undefined ? String(row.inverter_generation_600v) : '',
                    'Feeder1 Initial import kWh': row.feeder1_initial_import_kwh !== null && row.feeder1_initial_import_kwh !== undefined ? String(row.feeder1_initial_import_kwh) : '',
                    'Feeder1 Final import kWh': row.feeder1_final_import_kwh !== null && row.feeder1_final_import_kwh !== undefined ? String(row.feeder1_final_import_kwh) : '',
                    'B01Ff1 Net Import kWh': row['Net Import'] !== null && row['Net Import'] !== undefined ? String(row['Net Import']) : ''
                  };
                  if (!loadedKeys.includes(dateKey)) {
                    loadedKeys.push(dateKey);
                  }
                }
              });
            }
          }

          // Merge with existing data to preserve any unsaved changes
          if (Object.keys(loadedData).length > 0) {
            // Filter out removed rows before merging
            const { data: filteredLoadedData, rowKeys: filteredLoadedKeys } = filterRemovedRows(
              loadedData,
              loadedKeys,
              'Meter Reading',
              siteCode,
              loadUserId
            );

            // Clear justAddedRowRef for saved dates so real-time refresh can resume
            if (justAddedRowRef.current) {
              const savedDate = justAddedRowRef.current;
              const hasSavedRow = filteredLoadedKeys.includes(savedDate);
              if (hasSavedRow) {
                MTR_DEBUG && console.log(`✅ Cleared justAddedRowRef after saving date: ${savedDate}`);
                justAddedRowRef.current = null;
              }
            }

            const mergedData = { ...data, ...filteredLoadedData };
            const mergedKeys = [...new Set([...rowKeys, ...filteredLoadedKeys])].sort();
            setAllData(mergedData, mergedKeys);
          }
        }
      }

    } catch (error: any) {
      console.error('❌ Save error:', error);
      setStatus({
        type: 'error',
        message: `❌ Save failed: ${error.message}`
      });
    } finally {
      setIsExporting(false);
    }
  };

  /**
   * Export data to CSV
   */
  const exportData = async () => {
    setIsExporting(true);

    try {
      await save();
      const result = await meterReadingService.export(rowKeys);

      if (result.ok) {
        setStatus({ type: 'success', message: '✅ Export completed successfully!' });
      } else {
        setStatus({ type: 'error', message: `❌ Export failed: ${result.error}` });
      }
    } catch (error: any) {
      console.error('❌ Export error:', error);
      setStatus({ type: 'error', message: `❌ Export failed: ${error.message}` });
    } finally {
      setIsExporting(false);
    }
  };

  /**
   * Sort rows by date (ascending order)
   * Collects date values directly from frontend date inputs and sorts based on those values
   * 
   * IMPORTANT: This is FRONTEND-ONLY sorting. It only affects the display order.
   * - Does NOT call any backend APIs
   * - Does NOT modify backend data
   * - Does NOT affect save/load operations (data is saved/loaded based on row content, not order)
   * - Only reorders the visual display of rows in the table
   */
  const sortRowsByDate = () => {
    MTR_DEBUG && console.log('🔧 Manual sort triggered - collecting dates from frontend inputs');

    // Get the latest state values to ensure we have current data
    const currentRowKeys = [...rowKeys];
    const currentData = { ...data };

    if (currentRowKeys.length === 0) {
      setStatus({ type: 'warning', message: 'No rows to sort' });
      return;
    }

    // Create an array of rows with their actual date values from frontend inputs
    // Priority: 1) Date field in row data (from calendar input), 2) date from row key
    const rowsWithDates = currentRowKeys.map(key => {
      const rowData = currentData[key];

      // Get date from frontend input (Date column value) - this is the actual selected date
      let dateValue = '';
      if (rowData?.Date && /^\d{4}-\d{2}-\d{2}$/.test(String(rowData.Date))) {
        dateValue = String(rowData.Date);
      } else {
        // Fallback to using row key as date (rowKeys are dates in MeterReadingForm)
        dateValue = key;
      }

      return {
        key,
        date: dateValue,
        data: rowData
      };
    });

    MTR_DEBUG && console.log('📅 Collected dates from frontend:', rowsWithDates.map(r => ({
      key: r.key,
      date: r.date
    })));

    // Sort rows by date (ascending)
    rowsWithDates.sort((a, b) => {
      // Compare dates
      if (a.date !== b.date) {
        // Validate date format (YYYY-MM-DD) and use appropriate comparison
        if (/^\d{4}-\d{2}-\d{2}$/.test(a.date) && /^\d{4}-\d{2}-\d{2}$/.test(b.date)) {
          return a.date.localeCompare(b.date);
        }
        // Fallback to Date object comparison for other formats
        const dateAObj = new Date(a.date).getTime();
        const dateBObj = new Date(b.date).getTime();
        if (!isNaN(dateAObj) && !isNaN(dateBObj)) {
          return dateAObj - dateBObj;
        }
        // Final fallback to string comparison
        return a.date.localeCompare(b.date);
      }
      return 0; // Same date, maintain order
    });

    // Extract sorted keys
    const expectedKeys = rowsWithDates.map(row => row.key);

    // Check if already sorted
    const isAlreadySorted = JSON.stringify(expectedKeys) === JSON.stringify(currentRowKeys);
    if (isAlreadySorted) {
      setStatus({ type: 'info', message: 'Rows are already sorted by date' });
      return;
    }

    // Create new data object with sorted keys (preserve all data)
    const sortedData: any = {};
    expectedKeys.forEach(key => {
      if (currentData[key]) {
        sortedData[key] = currentData[key];
      }
    });

    MTR_DEBUG && console.log('🔄 Manual sort - reordering:', {
      before: currentRowKeys.map(k => {
        const row = rowsWithDates.find(r => r.key === k);
        return row ? row.date : k;
      }),
      after: expectedKeys.map(k => {
        const row = rowsWithDates.find(r => r.key === k);
        return row ? row.date : k;
      }),
      count: expectedKeys.length
    });

    // Set flag to prevent auto-sort from interfering
    isManuallySortingRef.current = true;

    // Update state in sequence to ensure proper re-render
    setTimeout(() => {
      // First update the data and rowKeys together
      setAllData(sortedData, expectedKeys);

      // Then update local state
      setRowKeysLocal(expectedKeys);

      // Force table re-render by incrementing tableKey
      setTableKey(prev => prev + 1);

      setTimeout(() => {
        isManuallySortingRef.current = false;
        setStatus({ type: 'success', message: `✅ Sorted ${expectedKeys.length} row(s) by date!` });
        MTR_DEBUG && console.log('✅ Sort completed - rows reordered by frontend date values:',
          expectedKeys.map(k => {
            const row = rowsWithDates.find(r => r.key === k);
            return row ? row.date : k;
          }).slice(0, 10)
        );
      }, 150);
    }, 0);
  };

  /**
   * Fetch and show summary modal
   */
  const fetchSummary = async () => {
    setLoadingSummary(true);

    try {
      const res = await meterReadingService.loadAll();

      if (res.ok && res.data) {
        setSummaryData(res.data);
        setShowSummary(true);
      } else {
        setStatus({ type: 'error', message: `❌ Failed to load summary: ${res.error}` });
      }
    } catch (error: any) {
      console.error('❌ Summary fetch error:', error);
      setStatus({ type: 'error', message: `❌ Failed to load summary: ${error.message}` });
    } finally {
      setLoadingSummary(false);
    }
  };

  // Use already loaded data for Correct Mistake (optimization - no backend call)
  const handleCorrectMistake = () => {
    try {
      // Check if we have any data loaded
      if (rowKeys.length === 0) {
        setStatus({ type: 'warning', message: 'No data available to edit. Please add some data first.' });
        return;
      }

      // Convert current table data to array format for modal
      const dataArray = rowKeys.map(key => ({
        ...data[key],
        entry_date: key,
        id: data[key]?.id || undefined
      }));

      MTR_DEBUG && console.log('📋 Using loaded Meter Reading data for edit:', dataArray.length, 'rows');
      setCorrectMistakeData(dataArray);
      setShowCorrectMistake(true);
    } catch (error: any) {
      setStatus({ type: 'error', message: `Error: ${error.message}` });
    }
  };

  // Save corrected data - update all rows that have IDs
  const handleSaveCorrectedData = async (updatedData: any[]) => {
    try {
      if (updatedData.length === 0) {
        throw new Error('No data to save');
      }

      const results: { success: number; failed: number; errors: string[] } = {
        success: 0,
        failed: 0,
        errors: []
      };

      // Process all rows that have IDs
      for (const row of updatedData) {
        if (row.id && typeof row.id === 'number') {
          try {
            // Map UI column keys back to backend field names
            const entryDate = row.entry_date || row.Date;
            const backendData: MeterReadingDataRow = {
              id: row.id,
              entry_date: entryDate,
              site_code: siteCode,
              feeder1_initial_export: row['Feeder1 Initial Export'] ? parseFloat(row['Feeder1 Initial Export']) : null,
              feeder1_final_export: row['Feeder1 Final Export'] ? parseFloat(row['Feeder1 Final Export']) : null,
              feeder1_net_kwh: row['Feeder1 Net kWh'] ? parseFloat(row['Feeder1 Net kWh']) : null,
              inverter_generation_600v: row['Inverter Generation 600V'] ? parseFloat(row['Inverter Generation 600V']) : null,
              feeder1_initial_import_kwh: row['Feeder1 Initial import kWh'] ? parseFloat(row['Feeder1 Initial import kWh']) : null,
              feeder1_final_import_kwh: row['Feeder1 Final import kWh'] ? parseFloat(row['Feeder1 Final import kWh']) : null,
              'Net Import': denormalizeNetImport(row['B01Ff1 Net Import kWh'])
            };

            MTR_DEBUG && console.log(`🔄 Updating Meter Reading record with ID: ${row.id}`);
            const updateResult = await meterReadingService.update(row.id, backendData);
            if (updateResult.ok) {
              results.success++;
              MTR_DEBUG && console.log(`✅ Updated Meter Reading data for ${entryDate} (ID: ${row.id})`);
            } else {
              results.failed++;
              results.errors.push(`Row ${row.id} (${entryDate}): ${updateResult.error || 'Update failed'}`);
            }
          } catch (error: any) {
            results.failed++;
            results.errors.push(`Row ${row.id} (${row.entry_date || row.Date}): ${error.message}`);
          }
        } else {
          MTR_DEBUG && console.log(`⚠️ Skipping row without ID: ${row.entry_date || row.Date}`);
        }
      }

      if (results.success > 0) {
        const message = results.failed > 0
          ? `✅ Updated ${results.success} record(s). ${results.failed} failed.`
          : `✅ Successfully updated ${results.success} record(s)!`;
        setStatus({ type: results.failed > 0 ? 'error' : 'success', message });
        if (results.errors.length > 0) {
          console.error('Update errors:', results.errors);
        }
      } else {
        throw new Error(`Failed to update any records. ${results.errors.join('; ')}`);
      }
    } catch (error: any) {
      throw new Error(`Failed to save: ${error.message}`);
    }
  };

  return (
    <div className="w-full h-full flex flex-col overflow-hidden relative">
      <div className="w-full h-full flex flex-col overflow-hidden">
        {/* Header */}
        <div
          className="text-white px-3 pt-2 pb-0"
          style={{ background: 'linear-gradient(to right, var(--color-primary-blue), var(--color-secondary-tata-blue))' }}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Zap size={16} />
              <div>
                <h1 className="text-sm font-bold" style={{ color: '#ffffff', fontWeight: 700 }}><strong>Plant Meters</strong></h1>
                <p className="text-xs" style={{ color: '#ffffff', opacity: 0.95 }}>{siteName} - 220kV MFM, ABT Inverter</p>
              </div>
            </div>
            <FormActions
              isCondensed={isCondensed}
              onToggleView={() => setIsCondensed(!isCondensed)}
              isExporting={isExporting}
              loadingSummary={loadingSummary}
              hasData={true}
              onAddRow={handleAddRow}
              onShowSummary={fetchSummary}
              onSave={save}
              onExport={exportData}
              addRowLabel="New Date"
              customActions={
                <>
                  <DateRangeFilter
                    startDate={startDate}
                    endDate={endDate}
                    onDateChange={(start: string | null, end: string | null) => {
                      setStartDate(start);
                      setEndDate(end);
                    }}
                  />
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={handleCorrectMistake}
                    disabled={loadingCorrectMistake}
                    className="flex items-center gap-1 px-3 py-1 rounded text-xs text-white font-medium transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{ background: 'var(--color-secondary-orange)' }}
                    title="Edit previously entered data"
                  >
                    {loadingCorrectMistake ? (
                      <div className="animate-spin h-3 w-3 border-2 border-white border-t-transparent rounded-full"></div>
                    ) : (
                      <Edit size={12} />
                    )}
                    <span className="hidden sm:inline">Edit</span>
                  </motion.button>
                </>
              }
            />
          </div>
        </div>

        {/* Status Banner */}
        {status.type && (
          <StatusBanner
            status={status.type}
            message={status.message}
            onClose={() => setStatus({ type: null, message: '' })}
          />
        )}

        {/* Table */}
        <div
          className="flex-1 min-h-0 w-full"
          style={{
            position: 'relative',
            scrollBehavior: 'auto' // Disable smooth scrolling to prevent animation
          }}
        >
          <UniversalTable
            columns={METER_READING_COLUMNS}
            data={data}
            rowKeys={(startDate || endDate) ? filteredRowKeys : memoizedRowKeys}
            styleConfig={isCondensed ? DEFAULT_STYLE_CONFIG : EXPANDED_STYLE_CONFIG}
            loading={isInitialLoad}
            focusedCell={focusedCell}
            onCellChange={updateCellWithCalculation} // FIXED: Now uses correct signature
            onRowKeyChange={handleRowKeyChange}
            onRemoveRowKey={handleRemoveRow}
            onKeyDown={handleKeyDown}
            onFocus={handleTableFocus}
            setInputRef={registerInput}
            containerRef={containerRef}
          />
        </div>

        {/* Summary Modal */}
        {showSummary && (
          <SummaryModal
            isOpen={showSummary}
            onClose={() => setShowSummary(false)}
            title="Plant Meters Summary - axon"
            data={summaryData}
            columns={[
              { key: 'entry_date', label: 'Date' },
              { key: 'feeder1_net_kwh', label: 'Net Export (kWh)', format: (v) => v ? Number(v).toFixed(2) : '-' },
              { key: 'inverter_generation_600v', label: 'Generation (kWh)', format: (v) => v ? Number(v).toFixed(2) : '-' },
              { key: 'Net Import', label: 'Net Import (kWh)', format: (v) => v ? Number(v).toFixed(2) : '-' }
            ]}
          />
        )}

        {/* Correct Mistake Modal */}
        {showCorrectMistake && (
          <CorrectMistakeModal
            isOpen={showCorrectMistake}
            onClose={() => setShowCorrectMistake(false)}
            title="Edit - Plant Meters"
            data={correctMistakeData}
            columns={METER_READING_COLUMNS}
            onSave={handleSaveCorrectedData}
            isLoading={loadingCorrectMistake}
            siteCode="axon"
          />
        )}

        {/* Footer */}
        <div className="px-4 py-2 text-xs border-t" style={{ background: 'var(--color-gray-100)', color: '#000000', fontWeight: 400 }}>
          <span>{footerText}</span>
        </div>
      </div>

      {/* Delete Row Dialog */}
      <DeleteRowDialog
        isOpen={showDeleteDialog}
        rowKey={rowToDelete?.key || ''}
        onClose={() => {
          setShowDeleteDialog(false);
          setRowToDelete(null);
        }}
        onPermanent={handlePermanentDelete}
        onTemporary={handleTemporaryRemove}
      />
    </div>
  );
};

